// DEPENDENCIES
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Checkbox from "@mui/material/Checkbox";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// API
import { getAPICall, postAPICall } from "../../config/apiCalls";
import { UserContext } from "../../context/UserContext";

// CSS
import "./WorkloadViewer.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const MS_PER_DAY  = 86400000;
const LEFT_W      = 160;
const ROW_H       = 64;
const SUB_ROW_H   = 62;
const BUDGET_W    = 150;
const DATES_W     = 100;
const BAR_MAX_H   = ROW_H - 14;

const PX_PER_DAY = { day: 34, week: 20, month: 5.6 };

const PROJECT_COLORS = [
	"#1565c0", "#6a1b9a", "#e65100", "#00838f",
	"#ad1457", "#4527a0", "#2e7d32", "#00695c",
	"#f57f17", "#bf360c", "#1a237e", "#558b2f",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const addDays      = (d, n) => new Date(d.getTime() + n * MS_PER_DAY);
const daysBetween  = (a, b) => Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
const dateToIso    = (d) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseDate    = (s) => { const p = String(s).split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); };
const isWeekend    = (d) => d.getDay() === 0 || d.getDay() === 6;
const fmtShort     = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtMonthYear = (d) => d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
const fmtH         = (n) => (Math.round(n * 10) / 10).toString();

const getToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// ─── Project status ───────────────────────────────────────────────────────────
// Collapses BQE's phase statuses down to the three tiers this view distinguishes:
// "active" and "on_hold" are always shown; anything else (Completed, Inactive,
// Canceled) is grouped as "other" and gated behind the "Show inactive" toggle.

const normalizeProjectStatus = (raw) => {
	const s = (raw || "").toLowerCase().trim();
	if (!s || s === "active") return "active";
	if (s === "on hold") return "on_hold";
	return "other";
};
const PROJECT_STATUS_RANK  = { active: 0, on_hold: 1, other: 2 };
const PROJECT_STATUS_LABEL = { active: "Active", on_hold: "On Hold" };
const PROJECT_STATUS_CHIP_COLORS = {
	active:  { chipBg: "#dcfce7", chipBorder: "#4ea861", chipText: "#14532d" },
	on_hold: { chipBg: "#fef3c7", chipBorder: "#d69e2e", chipText: "#7c5e10" },
};

const renderStatusChip = (status) => {
	const label = PROJECT_STATUS_LABEL[status];
	if (!label) return null;
	const tint = PROJECT_STATUS_CHIP_COLORS[status];
	return (
		<span style={{
			display: "inline-block", padding: "1px 6px", borderRadius: 10,
			fontSize: 10, fontWeight: 700, lineHeight: 1.5,
			background: tint.chipBg, border: `1px solid ${tint.chipBorder}`, color: tint.chipText,
		}}>
			{label}
		</span>
	);
};

// ─── Utilization tints ────────────────────────────────────────────────────────

const PLAN_HATCH = "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.45) 3px, rgba(255,255,255,0.45) 6px)";

// Four stepped bar heights so light weeks still produce a visible bar.
// Thresholds are total content hours; heights are fractions of BAR_MAX_H.
const tieredBarH = (hours) => {
	if (hours < 0.05) return 0;
	if (hours < 10)   return Math.round(BAR_MAX_H * 0.25); // ~12px
	if (hours < 20)   return Math.round(BAR_MAX_H * 0.50); // ~25px
	if (hours < 30)   return Math.round(BAR_MAX_H * 0.75); // ~37px
	return BAR_MAX_H;                                        // 50px
};

const UTIL_UNDER      = 0.65;
const UTIL_OVER       = 0.80;
const UTIL_OVERLOADED = 0.90;

const UTIL_STYLES = {
	under:      { bg: "#93c5fd", border: "#2563eb", text: "#1e3a8a" },
	onTrack:    { bg: "#86efac", border: "#16a34a", text: "#14532d" },
	over:       { bg: "#fdba74", border: "#ea580c", text: "#7c2d12" },
	overloaded: { bg: "#fca5a5", border: "#dc2626", text: "#7f1d1d" },
};

const getUtilStyle = (futurePlan, capacity) => {
	if (futurePlan < 0.05 || capacity === 0) return null;
	const u = futurePlan / capacity;
	if (u > UTIL_OVERLOADED) return UTIL_STYLES.overloaded;
	if (u > UTIL_OVER)       return UTIL_STYLES.over;
	if (u >= UTIL_UNDER)     return UTIL_STYLES.onTrack;
	return UTIL_STYLES.under;
};
const defaultFromStr = () => `${new Date().getFullYear()}-01-01`;
const defaultToStr   = () => `${new Date().getFullYear() + 1}-12-31`;

// Mirrors buildPhaseBrowserRow: strips the property prefix and any leading pipe
// segment from BQE_Parent to produce the short project name (e.g. "23-001").
const extractProjectName = (propertyName, bqeParent) => {
	if (!bqeParent) return "";
	const prefix = propertyName ? `${propertyName} | ` : "";
	const stripped = prefix && bqeParent.startsWith(prefix) ? bqeParent.slice(prefix.length) : bqeParent;
	const lastPipe = stripped.lastIndexOf(" | ");
	return lastPipe !== -1 ? stripped.slice(lastPipe + 3) : stripped;
};

// Normalize a BQE resource name for matching against the employees table.
// Handles "Last, First Middle" → "first middle last" and plain "First Last" → "first last".
const normalizeName = (n) => {
	if (!n) return "";
	const s = n.trim();
	if (s.includes(",")) {
		const comma = s.indexOf(",");
		const last  = s.slice(0, comma).trim();
		const rest  = s.slice(comma + 1).trim();
		return `${rest} ${last}`.toLowerCase();
	}
	return s.toLowerCase();
};

const nameTokens = (n) => {
	const normalized = normalizeName(n)
		.replace(/[.]/g, " ")
		.replace(/[^a-z\s'-]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	if (!normalized) return [];
	return normalized
		.split(" ")
		.map((token) => token.replace(/^['-]+|['-]+$/g, ""))
		.filter((token) => token && !["jr", "sr", "ii", "iii", "iv", "v"].includes(token));
};

const isPersonNameMatch = (a, b) => {
	const aTokens = nameTokens(a);
	const bTokens = nameTokens(b);
	if (aTokens.length === 0 || bTokens.length === 0) return false;

	const aFull = aTokens.join(" ");
	const bFull = bTokens.join(" ");
	if (aFull === bFull) return true;

	if (aTokens.length >= 2 && bTokens.length >= 2) {
		const aFirst = aTokens[0];
		const bFirst = bTokens[0];
		const aLast  = aTokens[aTokens.length - 1];
		const bLast  = bTokens[bTokens.length - 1];
		if (aFirst === bFirst && aLast === bLast) return true;
	}

	return false;
};

// ─── Build employee records from API data ─────────────────────────────────────

function buildEmpRecords(apiEmployees, apiEntries, timeOffMap, holidayList, apiBudgets, today, apiSpentHours, spentHoursLoaded) {
	// Build project registry in encounter order
	const projRegistry = new Map();
	apiEntries.forEach((e) => {
		if (e.project_id && !projRegistry.has(e.project_id)) {
			const idx = projRegistry.size;
			projRegistry.set(e.project_id, {
				id:             e.project_id,
				name:           e.project_name || e.project_id,
				projectName:    extractProjectName(e.project_name, e.bqe_parent),
				phaseName:      e.phase_name   || "",
				color:          PROJECT_COLORS[idx % PROJECT_COLORS.length],
				status:         normalizeProjectStatus(e.phase_status),
				manager_bqe_id: e.manager_bqe_id || null,
				manager:        e.manager || null,
			});
		}
	});
	// Apply schedule/status info from ALL budget rows (including schedule-only rows with
	// null employee) to registered projects. This ensures phases that have dates but no
	// budget allocation still show timeline markers for employees who logged time on them.
	(apiBudgets || []).forEach((b) => {
		const entry = projRegistry.get(b.project_id);
		if (!entry) return;
		if (b.phase_status)    entry.status         = normalizeProjectStatus(b.phase_status);
		if (b.manager_bqe_id) entry.manager_bqe_id = b.manager_bqe_id;
		if (b.manager)        entry.manager        = b.manager;
		if (b.phase_name && !entry.phaseName) entry.phaseName = b.phase_name;
		if (b.project_start) {
			const cs = parseDate(b.project_start);
			if (!entry.startDate || cs < entry.startDate) entry.startDate = cs;
			const ce = b.end_date ? parseDate(b.end_date) : null;
			if (ce && (!entry.endDate || ce > entry.endDate)) entry.endDate = ce;
		} else if (b.end_date) {
			const expEnd = parseDate(b.end_date);
			if (!entry.endDate || expEnd > entry.endDate) entry.endDate = expEnd;
		}
		if (b.on_hold_start) entry.onHoldStart = parseDate(b.on_hold_start);
		if (b.on_hold_end)   entry.onHoldEnd   = parseDate(b.on_hold_end);
	});

	// Index entries by resource_id (primary) and normalized name (fallback)
	const entriesByResourceId = new Map();
	const entriesByName       = new Map();
	apiEntries.forEach((entry) => {
		if (entry.resource_id) {
			const key = String(entry.resource_id);
			if (!entriesByResourceId.has(key)) entriesByResourceId.set(key, []);
			entriesByResourceId.get(key).push(entry);
		}
		const nameKey = normalizeName(entry.employee);
		if (!entriesByName.has(nameKey)) entriesByName.set(nameKey, []);
		entriesByName.get(nameKey).push(entry);
	});

	// All-time spent lookup maps (resource-id first, name fallback)
	const allTimeSpentById   = new Map();
	const allTimeSpentByName = new Map();
	(apiSpentHours || []).forEach(r => {
		if (r.resource_id && r.project_id) {
			const k = `${r.resource_id}|${r.project_id}`;
			allTimeSpentById.set(k, (allTimeSpentById.get(k) || 0) + r.total_hours);
		}
		if (r.employee && r.project_id) {
			const k = `${normalizeName(r.employee)}|${r.project_id}`;
			allTimeSpentByName.set(k, (allTimeSpentByName.get(k) || 0) + r.total_hours);
		}
	});

	// Same for budgets
	const budgetsByResourceId = new Map();
	const budgetsByName       = new Map();
	(apiBudgets || []).forEach((b) => {
		if (b.resource_id) {
			const key = String(b.resource_id);
			if (!budgetsByResourceId.has(key)) budgetsByResourceId.set(key, []);
			budgetsByResourceId.get(key).push(b);
		}
		const nameKey = normalizeName(b.employee);
		if (!budgetsByName.has(nameKey)) budgetsByName.set(nameKey, []);
		budgetsByName.get(nameKey).push(b);
	});

	const empRecords = apiEmployees.map((emp) => {
		const bqeIdKey   = emp.bqe_id ? String(emp.bqe_id) : null;
		const empEntries = (bqeIdKey && entriesByResourceId.get(bqeIdKey))
			|| entriesByName.get(normalizeName(emp.name))
			|| [];
		const days         = {};
		const empProjectIds = new Set();

		// Accumulate time entries into daily buckets
		empEntries.forEach((entry) => {
			const iso = entry.date;
			if (!days[iso]) days[iso] = { bill: 0, nonbill: 0, projBill: {}, projNonbill: {} };
			if (entry.billable) {
				days[iso].bill += entry.hours;
				if (entry.project_id) {
					empProjectIds.add(entry.project_id);
					days[iso].projBill[entry.project_id] =
						(days[iso].projBill[entry.project_id] || 0) + entry.hours;
				}
			} else {
				days[iso].nonbill += entry.hours;
				if (entry.project_id) {
					if (!days[iso].projNonbill) days[iso].projNonbill = {};
					days[iso].projNonbill[entry.project_id] =
						(days[iso].projNonbill[entry.project_id] || 0) + entry.hours;
				}
			}
		});

		// Overlay BambooHR time off
		(timeOffMap[emp.name] || []).forEach((period) => {
			let d = parseDate(period.start);
			const end = parseDate(period.end);
			while (d <= end) {
				const iso = dateToIso(d);
				if (!days[iso]) days[iso] = { bill: 0, nonbill: 0, projBill: {} };
				days[iso].off = 1;
				d = addDays(d, 1);
			}
		});

		// Overlay company holidays
		holidayList.forEach((h) => {
			let d = parseDate(h.start);
			const end = parseDate(h.end);
			while (d <= end) {
				const iso = dateToIso(d);
				if (!days[iso]) days[iso] = { bill: 0, nonbill: 0, projBill: {} };
				days[iso].holiday = 1;
				d = addDays(d, 1);
			}
		});

		// ── Projected work from remaining budget ─────────────────────────
		// Gated on spentHoursLoaded: "remaining" below is budget_hours minus hours
		// already logged, so computing it before spent-hours has arrived would treat
		// everyone as having logged 0 hours, spreading the FULL budget over the
		// remaining days and briefly showing inflated (100%+) projected percentages
		// until the real spent-hours total lands and this recomputes.
		if (apiBudgets && today && spentHoursLoaded) {
			const empBudgets = (bqeIdKey && budgetsByResourceId.get(bqeIdKey))
				|| budgetsByName.get(normalizeName(emp.name))
				|| [];

			empBudgets.forEach((budget) => {
				if (!budget.project_id) return;

				// Always link the employee to this project regardless of whether we can
				// compute a projection (budget used up, project ended, no schedule info).
				empProjectIds.add(budget.project_id);

				// Always register project and keep its schedule info current.
				if (!projRegistry.has(budget.project_id)) {
					const idx = projRegistry.size;
					projRegistry.set(budget.project_id, {
						id:             budget.project_id,
						name:           budget.project_name || budget.project_id,
						projectName:    extractProjectName(budget.project_name, budget.bqe_parent),
						phaseName:      budget.phase_name   || "",
						color:          PROJECT_COLORS[idx % PROJECT_COLORS.length],
						status:         normalizeProjectStatus(budget.phase_status),
						manager_bqe_id: budget.manager_bqe_id || null,
						manager:        budget.manager || null,
					});
				}
				if (budget.phase_status || budget.manager_bqe_id || budget.manager) {
					const reg = projRegistry.get(budget.project_id);
					if (reg) {
						if (budget.phase_status)    reg.status         = normalizeProjectStatus(budget.phase_status);
						if (budget.manager_bqe_id)  reg.manager_bqe_id = budget.manager_bqe_id;
						if (budget.manager)         reg.manager        = budget.manager;
					}
				}
				{
					const entry = projRegistry.get(budget.project_id);
					if (budget.project_start) {
						const candidateStart = parseDate(budget.project_start);
						if (!entry.startDate || candidateStart < entry.startDate) entry.startDate = candidateStart;
						const candidateEnd = budget.end_date ? parseDate(budget.end_date) : null;
						if (candidateEnd && (!entry.endDate || candidateEnd > entry.endDate)) entry.endDate = candidateEnd;
					} else if (budget.end_date) {
						const expEnd = parseDate(budget.end_date);
						if (!entry.endDate || expEnd > entry.endDate) entry.endDate = expEnd;
					}
					if (budget.on_hold_start) entry.onHoldStart = parseDate(budget.on_hold_start);
					if (budget.on_hold_end)   entry.onHoldEnd   = parseDate(budget.on_hold_end);
				}

				// Projection bars require an explicit end date (consistent with project viewer).
				if (!budget.end_date) return;

				const ridKey      = bqeIdKey ? `${bqeIdKey}|${budget.project_id}` : null;
				const allTimeSpent = (ridKey && allTimeSpentById.has(ridKey))
					? allTimeSpentById.get(ridKey)
					: (allTimeSpentByName.get(`${normalizeName(emp.name)}|${budget.project_id}`) || 0);
				const remaining = budget.budget_hours - allTimeSpent;
				if (remaining <= 0.1) return;

				const projStart = budget.project_start
					? parseDate(budget.project_start)
					: (() => {
						const first = empEntries
							.filter(e => e.project_id === budget.project_id)
							.reduce((min, e) => (!min || e.date < min ? e.date : min), null);
						return first ? parseDate(first) : null;
					})();
				if (!projStart) return;
				const projEnd = parseDate(budget.end_date);
				if (projEnd <= today) return;

				// Project-wide on-hold window (server only sends these when the phase is
				// currently on hold AND both dates are set — see workload.js) — days inside
				// it are excluded from this project's projection, same rule as the Schedule
				// tab's blackout so the two stay consistent.
				const onHoldStart = budget.on_hold_start ? parseDate(budget.on_hold_start) : null;
				const onHoldEnd   = budget.on_hold_end   ? parseDate(budget.on_hold_end)   : null;
				const isOnHoldDay = (day) => onHoldStart && onHoldEnd && day >= onHoldStart && day <= onHoldEnd;

				// Count available future workdays (today → projEnd, skip weekends/off/holidays/on-hold)
				let availDays = 0;
				let d = new Date(today);
				while (d < projEnd) {
					if (!isWeekend(d) && !isOnHoldDay(d)) {
						const iso = dateToIso(d);
						const rec = days[iso];
						if (!rec?.holiday && !rec?.off) availDays++;
					}
					d = addDays(d, 1);
				}
				if (availDays === 0) return;

				const hPerDay = remaining / availDays;

				// Write projected hours into future workdays
				d = new Date(today);
				while (d < projEnd) {
					if (!isWeekend(d) && !isOnHoldDay(d)) {
						const iso = dateToIso(d);
						if (!days[iso]) days[iso] = { bill: 0, nonbill: 0, projBill: {} };
						const rec = days[iso];
						if (!rec.holiday && !rec.off) {
							rec.plan = (rec.plan || 0) + hPerDay;
							if (!rec.projPlan) rec.projPlan = {};
							rec.projPlan[budget.project_id] = (rec.projPlan[budget.project_id] || 0) + hPerDay;
						}
					}
					d = addDays(d, 1);
				}
			});
		}

		const empBudgetsForName = (bqeIdKey && budgetsByResourceId.get(bqeIdKey))
				|| budgetsByName.get(normalizeName(emp.name))
				|| [];
		const bqeName = empEntries[0]?.employee || empBudgetsForName[0]?.employee || null;

		return {
			id:             emp.id,
			name:           emp.name,
			office:         emp.office    || "",
			role:           emp.job_title || "",
			hoursPerDay:    emp.hours_per_week ? Number(emp.hours_per_week) / 5 : 8,
			hoursPerWeek:   emp.hours_per_week ?? null,
			employmentType: emp.employment_type ?? null,
			projects:       [...empProjectIds],
			days,
			bqeId:          bqeIdKey,
			bqeName,
		};
	});

	const projects = [...projRegistry.values()];
	return { empRecords, projects };
}

// ─── Aggregate a date range for one employee ──────────────────────────────────
// Counts capacity from the date loop itself so days with no entries still
// contribute to the workday baseline (8h/day standard).

function aggregateRange(emp, from, to) {
	const hpd = emp.hoursPerDay ?? 8;
	let bill = 0, nonbill = 0, futurePlan = 0, offDays = 0;
	let capacity = 0, offH = 0, holidayH = 0, billCap = 0;
	const projBill = {};
	const projNonbill = {};
	const projPlan = {};
	let d = new Date(from);
	while (d < to) {
		const iso = dateToIso(d);
		const rec = emp.days[iso];
		if (!isWeekend(d)) {
			billCap += hpd;
			if (rec?.holiday) {
				holidayH += hpd;
			} else if (rec?.off) {
				offH += hpd;
				offDays++;
			} else {
				capacity += hpd;
				if (rec?.plan) futurePlan += rec.plan;
				if (rec?.projPlan) {
					Object.entries(rec.projPlan).forEach(([pid, h]) => {
						projPlan[pid] = (projPlan[pid] || 0) + h;
					});
				}
			}
		}
		// Logged hours count regardless of weekday/weekend — only capacity accrual above is weekday-only.
		if (rec) {
			bill    += rec.bill    || 0;
			nonbill += rec.nonbill || 0;
			if (rec.projBill) {
				Object.entries(rec.projBill).forEach(([pid, h]) => {
					projBill[pid] = (projBill[pid] || 0) + h;
				});
			}
			if (rec.projNonbill) {
				Object.entries(rec.projNonbill).forEach(([pid, h]) => {
					projNonbill[pid] = (projNonbill[pid] || 0) + h;
				});
			}
		}
		d = addDays(d, 1);
	}
	return { bill, nonbill, futurePlan, offDays, capacity, offH, holidayH, billCap, projBill, projNonbill, projPlan };
}

// ─── Aggregate a date range across multiple employees (office view) ──────────

function aggregateOffice(employees, from, to) {
	let bill = 0, nonbill = 0, futurePlan = 0, offDays = 0;
	let capacity = 0, offH = 0, holidayH = 0, billCap = 0;
	employees.forEach(emp => {
		const agg = aggregateRange(emp, from, to);
		bill       += agg.bill;
		nonbill    += agg.nonbill;
		futurePlan += agg.futurePlan;
		offDays    += agg.offDays;
		capacity   += agg.capacity;
		offH       += agg.offH;
		holidayH   += agg.holidayH;
		billCap    += agg.billCap;
	});
	return { bill, nonbill, futurePlan, offDays, capacity, offH, holidayH, billCap };
}

// ─── Aggregate a project's hours across employees for one time bucket ─────────

function aggregateProject(employees, projectId, from, to) {
	let bill = 0, futurePlan = 0;
	employees.forEach(emp => {
		const agg = aggregateRange(emp, from, to);
		bill       += agg.projBill[projectId] || 0;
		futurePlan += agg.projPlan[projectId]  || 0;
	});
	return { bill, futurePlan };
}

// ─── Build visible time buckets ───────────────────────────────────────────────

function buildBuckets(zoom, dataStart, scrollDays, visibleDays) {
	const startDay = Math.floor(scrollDays) - 1;
	const endDay   = Math.ceil(scrollDays + visibleDays) + 1;
	const buckets  = [];

	if (zoom === "day") {
		for (let d = startDay; d <= endDay; d++) {
			const date     = addDays(dataStart, d);
			const dow      = date.getDay();
			const isFirst  = d === startDay || date.getDate() === 1;
			// Show month abbreviation on the 1st of the month (or first visible day)
			const label = isFirst
				? date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
				: String(date.getDate());
			buckets.push({
				startDay: d, endDay: d + 1,
				startDate: date, endDate: addDays(date, 1),
				label,
				sub:   ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][dow],
				isWeekend: dow === 0 || dow === 6,
			});
		}
	} else if (zoom === "week") {
		const anchor    = addDays(dataStart, startDay);
		const dow       = anchor.getDay();
		const mondayOff = startDay - (dow === 0 ? 6 : dow - 1);
		for (let w = mondayOff; w <= endDay; w += 7) {
			const ws       = addDays(dataStart, w);
			const we       = addDays(ws, 7);
			const endLabel = addDays(ws, 6);
			const crossMonth = ws.getMonth() !== endLabel.getMonth();
			const label = crossMonth
				? `${fmtShort(ws)} – ${fmtShort(endLabel)}`
				: `${ws.toLocaleDateString("en-US", { month: "short" })} ${ws.getDate()}–${endLabel.getDate()}`;
			buckets.push({ startDay: w, endDay: w + 7, startDate: ws, endDate: we, label, isWeekend: false });
		}
	} else {
		const anchor = addDays(dataStart, Math.max(0, startDay));
		let cur = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
		for (let iter = 0; iter < 24; iter++) {
			const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
			const sd   = daysBetween(dataStart, cur);
			const ed   = daysBetween(dataStart, next);
			if (sd > endDay + 35) break;
			buckets.push({ startDay: sd, endDay: ed, startDate: cur, endDate: next, label: fmtMonthYear(cur), isWeekend: false });
			cur = next;
		}
	}
	return buckets;
}

// ─── TimelineScrollbar ────────────────────────────────────────────────────────

function TimelineScrollbar({ value, max, thumbFraction, onChange, disabled }) {
	const trackRef = useRef(null);

	const onPointerDown = (e) => {
		if (disabled) return;
		const track = trackRef.current;
		if (!track) return;
		const rect   = track.getBoundingClientRect();
		const thumbW = rect.width * thumbFraction;
		const usable = rect.width - thumbW;
		let sv       = value;
		const startX = e.clientX;

		if (e.target?.dataset?.thumb !== "1" && usable > 0) {
			const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left - thumbW / 2) / usable));
			sv = ratio * max;
			onChange(sv);
		}

		const onMove = (ev) => {
			if (usable <= 0) return;
			onChange(Math.max(0, Math.min(max, sv + ((ev.clientX - startX) / usable) * max)));
		};
		const onUp = () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup",   onUp);
		};
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup",   onUp);
		e.preventDefault();
	};

	const ratio    = max > 0 ? Math.min(1, value / max) : 0;
	const thumbPct = Math.min(100, thumbFraction * 100);
	const leftPct  = ratio * (100 - thumbPct);

	return (
		<div
			ref={trackRef}
			onPointerDown={onPointerDown}
			style={{
				position: "relative", height: 16, background: "#eef0f3",
				border: "1px solid #d8dce3", borderRadius: 8,
				cursor: disabled ? "default" : "pointer",
				opacity: disabled ? 0.4 : 1, userSelect: "none", touchAction: "none",
			}}
		>
			<div
				data-thumb="1"
				style={{
					position: "absolute", top: 2, bottom: 2,
					left: `${leftPct}%`, width: `${thumbPct}%`,
					background: "#fff", border: "1px solid #b6bcc7",
					borderRadius: 6, cursor: disabled ? "default" : "grab",
					boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
				}}
			/>
		</div>
	);
}

// ─── Select-all sentinels ─────────────────────────────────────────────────────

const OFFICE_ALL = "__all_offices__";
const PERSON_ALL = { id: "__all_people__", name: "Select All", office: "", role: "" };

const ENTRY_TYPES = [
	{ id: "billable",    label: "Billable",     color: "#43a047" },
	{ id: "nonbillable", label: "Non-Billable",  color: "#ef5350" },
	{ id: "planned",     label: "Planned",      color: "#5b8dd6" },
	{ id: "timeoff",     label: "Time Off",     color: "#9575cd" },
	{ id: "holidays",    label: "Holidays",     color: "#f59e0b" },
];
const TYPE_ALL     = "__all_types__";
const ALL_TYPE_IDS = ENTRY_TYPES.map((t) => t.id);

// ─── LegendSwatch ─────────────────────────────────────────────────────────────

function LegendSwatch({ color, border, label, backgroundImage }) {
	return (
		<span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: "#000000" }}>
			<span style={{
				display: "inline-block", width: 16, height: 12,
				background: color, backgroundImage: backgroundImage,
				border: `1px solid ${border}`,
				borderRadius: 3, flexShrink: 0,
			}} />
			{label}
		</span>
	);
}

// ─── WorkloadViewer ───────────────────────────────────────────────────────────

const WorkloadViewer = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name:  "Workload Viewer",
			employeeID: employeeID,
			notes:      "Accessed the Workload Viewer page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Raw API data ─────────────────────────────────────────────────────────
	const [apiEmployees, setApiEmployees] = useState([]);
	const [apiEntries,   setApiEntries]   = useState([]);
	const [timeOffMap,   setTimeOffMap]   = useState({});
	const [holidayList,  setHolidayList]  = useState([]);
	const [apiBudgets,    setApiBudgets]    = useState([]);
	const [apiSpentHours, setApiSpentHours] = useState([]);
	const [spentHoursLoaded, setSpentHoursLoaded] = useState(false);
	const [budgetsLoaded,    setBudgetsLoaded]    = useState(false);
	const [employeesLoaded,  setEmployeesLoaded]  = useState(false);
	const [entriesLoadedOnce, setEntriesLoadedOnce] = useState(false);
	const [error,        setError]        = useState(null);
	const [adminJobTitleIds,     setAdminJobTitleIds]     = useState(new Set());
	const [adminJobTitlesLoaded, setAdminJobTitlesLoaded] = useState(false);

	// ── View / filter state ──────────────────────────────────────────────────
	const [zoom,        setZoom]        = useState("week");
	const [scrollDays,  setScrollDays]  = useState(0);
	const [selOffices,  setSelOffices]  = useState([]);
	const [selPeople,   setSelPeople]   = useState([]);
	const [officeInput, setOfficeInput] = useState("");
	const [peopleInput, setPeopleInput] = useState("");
	const [fromStr,     setFromStr]     = useState(defaultFromStr);
	const [toStr,       setToStr]       = useState(defaultToStr);
	const [expanded,           setExpanded]           = useState(new Set());
	const [showClosedProjects, setShowClosedProjects] = useState(false);
	const [trackW,             setTrackW]             = useState(600);
	const [selInitialized,     setSelInitialized]     = useState(false);
	const [selTypes,           setSelTypes]           = useState(ALL_TYPE_IDS);
	const [typeInput,          setTypeInput]          = useState("");
	const [wakeKey,            setWakeKey]            = useState(0);
	const [viewMode,           setViewMode]           = useState("person");

	const chartRef        = useRef(null);
	const fetchBudgetsRef = useRef(null);
	const lastHiddenRef   = useRef(null);

	// ── Derived values ───────────────────────────────────────────────────────
	const [TODAY, setToday] = useState(getToday);
	const fromDate = useMemo(() => parseDate(fromStr), [fromStr]);
	const toDate   = useMemo(() => parseDate(toStr),   [toStr]);
	const totalDays = useMemo(() => Math.max(1, daysBetween(fromDate, toDate)), [fromDate, toDate]);

	const { empRecords, projects } = useMemo(
		() => buildEmpRecords(apiEmployees, apiEntries, timeOffMap, holidayList, apiBudgets, TODAY, apiSpentHours, spentHoursLoaded),
		[apiEmployees, apiEntries, timeOffMap, holidayList, apiBudgets, TODAY, apiSpentHours, spentHoursLoaded]
	);

	const allOffices = useMemo(
		() => [...new Set(apiEmployees.map((e) => e.office).filter(Boolean))].sort(),
		[apiEmployees]
	);

	// Lookup maps for all-time spent hours — keyed by "resourceId|projectId" and
	// "normalizedName|projectId" so renderSubRow can do O(1) lookups.
	const spentByResourceId = useMemo(() => {
		const map = new Map();
		apiSpentHours.forEach(r => {
			if (!r.resource_id || !r.project_id) return;
			const key = `${r.resource_id}|${r.project_id}`;
			map.set(key, (map.get(key) || 0) + r.total_hours);
		});
		return map;
	}, [apiSpentHours]);

	const spentByName = useMemo(() => {
		const map = new Map();
		apiSpentHours.forEach(r => {
			if (!r.employee || !r.project_id) return;
			const key = `${normalizeName(r.employee)}|${r.project_id}`;
			map.set(key, (map.get(key) || 0) + r.total_hours);
		});
		return map;
	}, [apiSpentHours]);

	// ── Scroll / zoom math ───────────────────────────────────────────────────
	const pxPerDay    = PX_PER_DAY[zoom];
	const visibleDays = trackW / pxPerDay;

	const scrollPad = useMemo(() => {
		if (zoom === "day") return 1;
		if (zoom === "week") {
			const lastDow        = (fromDate.getDay() + (totalDays - 1)) % 7;
			const daysToWeekEnd  = (1 - lastDow + 7) % 7 || 7;
			return daysToWeekEnd - 1;
		}
		const nextMonth = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 1);
		return Math.round((nextMonth - toDate) / MS_PER_DAY);
	}, [zoom, fromDate, toDate, totalDays]);

	const maxScroll   = Math.max(0, totalDays + scrollPad - visibleDays);
	const dayToPx     = (day) => (day - scrollDays) * pxPerDay;
	const todayDayIdx = daysBetween(fromDate, TODAY);
	const todayPx     = dayToPx(todayDayIdx + 1);

	// ── Track width measurement ──────────────────────────────────────────────
	// Use chartRef (always in DOM) rather than trackRef (only exists after data loads).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (!chartRef.current) return;
		const w = Math.max(0, chartRef.current.clientWidth - LEFT_W - DATES_W - BUDGET_W);
		setTrackW((prev) => (prev === w ? prev : w));
	});

	useEffect(() => {
		const ro = new ResizeObserver(() => {
			if (chartRef.current) {
				setTrackW(Math.max(0, chartRef.current.clientWidth - LEFT_W - DATES_W - BUDGET_W));
			}
		});
		if (chartRef.current) ro.observe(chartRef.current);
		return () => ro.disconnect();
	}, []);

	// Renders a translucent on-hold band clipped to a project's own row segment —
	// unlike the Schedule tab (a single project's whole-chart band), each row here
	// can belong to a different project with its own on-hold window.
	const renderOnHoldBand = (proj) => {
		if (!proj.onHoldStart || !proj.onHoldEnd) return null;
		const startPx = dayToPx(daysBetween(fromDate, proj.onHoldStart));
		const endPx = dayToPx(daysBetween(fromDate, proj.onHoldEnd) + 1);
		if (endPx < 0 || startPx > trackW) return null;
		const cl = Math.max(0, startPx);
		const cw = Math.min(trackW, endPx) - cl;
		if (cw <= 0) return null;
		return (
			<Tooltip arrow placement="top" title={`On Hold — ${fmtShort(proj.onHoldStart)} – ${fmtShort(proj.onHoldEnd)}`}>
				<div
					style={{
						position: "absolute", left: cl, width: cw, top: 0, bottom: 0,
						background: "rgba(202, 138, 4, 0.10)",
						borderLeft: startPx >= 0 ? "1.5px solid rgba(202, 138, 4, 0.45)" : "none",
						borderRight: endPx <= trackW ? "1.5px solid rgba(202, 138, 4, 0.45)" : "none",
						pointerEvents: "auto", cursor: "default", boxSizing: "border-box", zIndex: 1,
					}}
				/>
			</Tooltip>
		);
	};

	// ── Scroll clamping & centering ──────────────────────────────────────────
	useEffect(() => {
		setScrollDays((s) => Math.max(0, Math.min(s, maxScroll)));
	}, [maxScroll]);

	const zoomInitRef = useRef(false);
	useEffect(() => {
		if (!zoomInitRef.current) { zoomInitRef.current = true; return; }
		setScrollDays(Math.max(0, Math.min(maxScroll, todayDayIdx - visibleDays / 2)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoom]);

	const centeredOnce = useRef(false);
	useEffect(() => {
		if (centeredOnce.current || trackW === 600) return;
		centeredOnce.current = true;
		setScrollDays(Math.max(0, Math.min(maxScroll, todayDayIdx - visibleDays / 2)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trackW]);

	// ── Data fetching ────────────────────────────────────────────────────────

	// Fetch employee list + admin job title exclusions once on mount
	useEffect(() => {
		const fetchEmployees = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], "/api/workload/employees");
				setApiEmployees(res.data || []);
			} catch (err) {
				console.error("[workload] employees fetch failed", err);
				setError("Failed to load employees.");
			} finally {
				setEmployeesLoaded(true);
			}
		};
		const fetchAdminJobTitles = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], "/api/workload/admin-job-titles");
				const ids = new Set((res.data || []).map((r) => r.job_title_id));
				setAdminJobTitleIds(ids);
			} catch (err) {
				console.error("[workload] admin job titles fetch failed", err);
			} finally {
				setAdminJobTitlesLoaded(true);
			}
		};
		const fetchSpentHours = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], "/api/workload/spent-hours");
				setApiSpentHours(res.data?.spent || []);
			} catch (err) {
				console.error("[workload] spent hours fetch failed:", err);
			} finally {
				setSpentHoursLoaded(true);
			}
		};
		fetchEmployees();
		fetchAdminJobTitles();
		fetchSpentHours();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Re-fetch fresh data when the tab becomes visible again after 5+ minutes hidden.
	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				lastHiddenRef.current = Date.now();
			} else {
				const hiddenMs = lastHiddenRef.current ? Date.now() - lastHiddenRef.current : 0;
				lastHiddenRef.current = null;
				setToday(getToday());
				if (fetchBudgetsRef.current) fetchBudgetsRef.current();
				if (hiddenMs > 5 * 60 * 1000) setWakeKey(k => k + 1);
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, []);

	// Fetch budget allocations from server cache — instant REST call, always warm.
	useEffect(() => {
		const fetchBudgets = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], "/api/workload/budgets");
				const rows = res.data?.budgets || [];
				setApiBudgets(rows);
				// Auto-extend toStr to cover the furthest project end date
				let maxEnd = null;
				rows.forEach((b) => {
					if (!b.end_date) return;
					const end = parseDate(b.end_date);
					if (end && (!maxEnd || end > maxEnd)) maxEnd = end;
				});
				if (maxEnd) {
					setToStr((prev) => {
						const maxIso = dateToIso(maxEnd);
						return maxIso > prev ? maxIso : prev;
					});
				}
			} catch (err) {
				console.error("[workload] budgets fetch failed", err);
			} finally {
				setBudgetsLoaded(true);
			}
		};
		fetchBudgetsRef.current = fetchBudgets;
		fetchBudgets();
		const interval = setInterval(fetchBudgets, 2 * 60 * 1000);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Initialise selections once both employees and admin job titles have loaded (one-time only).
	// All offices are selected by default; non-admin people are pre-selected.
	useEffect(() => {
		if (apiEmployees.length === 0 || !adminJobTitlesLoaded || selInitialized) return;
		setSelInitialized(true);
		setSelOffices(allOffices);
		setSelPeople(apiEmployees.filter((e) => !adminJobTitleIds.has(e.job_title_id)));
	}, [apiEmployees, adminJobTitleIds, adminJobTitlesLoaded, selInitialized, allOffices]);

	// Fetch time entries + time off whenever the date range, employee list, or selected offices change.
	// NocoDB (time entries) and BambooHR (time off) are fetched independently so entries appear
	// immediately without waiting for BambooHR to respond.
	useEffect(() => {
		if (apiEmployees.length === 0 || selOffices.length === 0) {
			setApiEntries([]);
			setTimeOffMap({});
			setHolidayList([]);
			return;
		}
		let cancelled = false;

		const fetchData = async () => {
			setError(null);
			const names = apiEmployees.filter(e => selOffices.includes(e.office)).map(e => e.name);

			const entriesPromise = getAPICall(instance, accounts[0], `/api/workload/time-entries?from=${fromStr}&to=${toStr}`)
				.then(res => { if (!cancelled) setApiEntries(res.data?.entries || []); })
				.catch(err => {
					if (!cancelled) {
						console.error("[workload] time-entries fetch failed", err);
						setError("Failed to load time entry data.");
					}
				});

			const bambooPromise = postAPICall(instance, accounts[0], "/api/bamboohr/schedule_time_off", {
				names, start: fromStr, end: toStr,
			})
				.then(res => {
					if (!cancelled) {
						setTimeOffMap(res.data?.timeOff || {});
						setHolidayList(res.data?.holidays || []);
					}
				})
				.catch(err => { if (!cancelled) console.error("[workload] bamboohr fetch failed", err); });

			await Promise.all([entriesPromise, bambooPromise]);
			if (!cancelled) setEntriesLoadedOnce(true);
		};
		fetchData();
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fromStr, toStr, selOffices, apiEmployees, wakeKey]);

	// ── Filtered data ────────────────────────────────────────────────────────
	const filteredEmployees = useMemo(
		() => empRecords
			.filter((e) => selOffices.includes(e.office) && selPeople.some((p) => p.id === e.id))
			.sort((a, b) => a.name.localeCompare(b.name)),
		[empRecords, selOffices, selPeople]
	);

	const filteredOffices = useMemo(() => {
		if (viewMode !== "office") return [];
		const nonAdminIds = new Set(
			apiEmployees.filter(e => !adminJobTitleIds.has(e.job_title_id)).map(e => e.id)
		);
		const byOffice = new Map();
		empRecords.forEach(emp => {
			if (!selOffices.includes(emp.office)) return;
			if (!nonAdminIds.has(emp.id)) return;
			if (!byOffice.has(emp.office)) byOffice.set(emp.office, []);
			byOffice.get(emp.office).push(emp);
		});
		return [...byOffice.entries()]
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([office, employees]) => ({ office, employees }));
	}, [viewMode, empRecords, selOffices, apiEmployees, adminJobTitleIds]);

	const filteredProjects = useMemo(() => {
		if (viewMode !== "project") return [];
		const nonAdminIds = new Set(
			apiEmployees.filter(e => !adminJobTitleIds.has(e.job_title_id)).map(e => e.id)
		);
		return projects
			.filter(proj => showClosedProjects || proj.status !== "other")
			.map(proj => ({
				proj,
				employees: empRecords
					.filter(emp =>
						emp.projects.includes(proj.id) &&
						selOffices.includes(emp.office) &&
						selPeople.some(p => p.id === emp.id) &&
						nonAdminIds.has(emp.id)
					)
					.sort((a, b) => {
						const isOwner = (emp) => {
							if (proj.manager_bqe_id && emp.bqeId) {
								return proj.manager_bqe_id === emp.bqeId;
							}
							const managerName = normalizeName(proj.manager);
							return !!managerName && [emp.name, emp.bqeName].some((name) => isPersonNameMatch(name, managerName));
						};
						const ownerDelta = Number(isOwner(b)) - Number(isOwner(a));
						if (ownerDelta !== 0) return ownerDelta;
						return a.name.localeCompare(b.name);
					}),
			}))
			.filter(({ employees }) => employees.length > 0)
			.sort((a, b) => {
				const rankDelta = (PROJECT_STATUS_RANK[a.proj.status] ?? 9) - (PROJECT_STATUS_RANK[b.proj.status] ?? 9);
				if (rankDelta !== 0) return rankDelta;
				return a.proj.name.localeCompare(b.proj.name);
			});
	}, [viewMode, projects, empRecords, selOffices, selPeople, apiEmployees, adminJobTitleIds, showClosedProjects]);

	const spentByProject = useMemo(() => {
		const map = new Map();
		apiSpentHours.forEach(r => {
			if (!r.project_id) return;
			map.set(r.project_id, (map.get(r.project_id) || 0) + r.total_hours);
		});
		return map;
	}, [apiSpentHours]);

	const budgetByProject = useMemo(() => {
		const map = new Map();
		apiBudgets.forEach(b => {
			if (!b.project_id || !b.budget_hours) return;
			map.set(b.project_id, (map.get(b.project_id) || 0) + b.budget_hours);
		});
		return map;
	}, [apiBudgets]);

	const buckets = useMemo(
		() => buildBuckets(zoom, fromDate, scrollDays, visibleDays),
		[zoom, fromDate, scrollDays, visibleDays]
	);

	// In day zoom the sub-row plan chips use week-granularity buckets so projected
	// hours render as one wide bar per week rather than tiny per-day chips.
	const planBuckets = useMemo(
		() => zoom === "day" ? buildBuckets("week", fromDate, scrollDays, visibleDays) : null,
		[zoom, fromDate, scrollDays, visibleDays]
	);

	const toggleExpand = (id) =>
		setExpanded((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

	const jumpToToday = () =>
		setScrollDays(Math.max(0, Math.min(maxScroll, todayDayIdx - visibleDays / 2)));

	// ── Render ────────────────────────────────────────────────────────────────

	const showBillable = selTypes.includes("billable");
	const showNonbill  = selTypes.includes("nonbillable");
	const showPlanned  = selTypes.includes("planned");
	const showTimeOff  = selTypes.includes("timeoff");
	const showHolidays = selTypes.includes("holidays");

	return (
		<div className="WorkloadViewer">

			{/* ── Page header ──────────────────────────────────────────────── */}
			<div className="wvPageHeader">
				<h1 className="wvTitle">Workload Viewer</h1>
				<div style={{ flex: 1 }} />
				<Tooltip title="Budget hours are synced from BQE by an hourly audit. Figures shown may be up to an hour old." placement="bottom" arrow>
					<span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#7a8190", whiteSpace: "nowrap", cursor: "default" }}>
						<InfoOutlinedIcon style={{ fontSize: 15, color: "#9ca3af" }} />
						Budget data updates automatically
					</span>
				</Tooltip>
				<ButtonGroup size="small" variant="outlined">
					<Button variant={viewMode === "person" ? "contained" : undefined} disableElevation={viewMode === "person"} onClick={() => setViewMode("person")}>By Person</Button>
					<Button variant={viewMode === "project" ? "contained" : undefined} disableElevation={viewMode === "project"} onClick={() => setViewMode("project")}>By Project</Button>
					<Button variant={viewMode === "office" ? "contained" : undefined} disableElevation={viewMode === "office"} onClick={() => setViewMode("office")}>By Office</Button>
				</ButtonGroup>
			</div>

			{/* ── Filter bar ───────────────────────────────────────────────── */}
			<div className="wvFilterBar">
				{/* Office filter */}
				<Autocomplete
					multiple disableCloseOnSelect size="small"
					options={[OFFICE_ALL, ...allOffices]}
					value={selOffices}
					onChange={(_, newValue) => {
						if (newValue.includes(OFFICE_ALL)) {
							setSelOffices(selOffices.length === allOffices.length ? [] : [...allOffices]);
						} else {
							setSelOffices(newValue);
						}
					}}
					inputValue={officeInput}
					onInputChange={(_, v) => setOfficeInput(v)}
					filterOptions={(_, { inputValue }) => {
						const q = inputValue.toLowerCase();
						return [OFFICE_ALL, ...allOffices.filter((o) => o.toLowerCase().includes(q))];
					}}
					getOptionLabel={(o) => (o === OFFICE_ALL ? "Select All" : o)}
					renderOption={(props, option, { selected }) => {
						if (option === OFFICE_ALL) {
							const allSel = selOffices.length === allOffices.length;
							return (
								<li {...props} key={OFFICE_ALL}>
									<Checkbox indeterminate={selOffices.length > 0 && !allSel} checked={allSel} size="small" sx={{ mr: 1 }} />
									<span style={{ fontSize: 13, fontWeight: 500 }}>Select All</span>
								</li>
							);
						}
						return (
							<li {...props}>
								<Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={selected} size="small" sx={{ mr: 1 }} />
								{option}
							</li>
						);
					}}
					renderTags={(value) => officeInput ? null : (
						<span style={{ fontSize: 13, paddingLeft: 2, whiteSpace: "nowrap", pointerEvents: "none" }}>
							{value.length === allOffices.length ? "All Offices" : value.join(", ")}
						</span>
					)}
					renderInput={(params) => <TextField {...params} placeholder={selOffices.length === 0 ? "Offices" : ""} />}
					sx={{ width: 250, "& .MuiInputBase-root": { flexWrap: "nowrap" }, "& .MuiAutocomplete-input": { minWidth: "0 !important" } }}
				/>

				{/* People filter — hidden in office view */}
				{viewMode !== "office" && <Autocomplete
					multiple disableCloseOnSelect size="small"
					options={[PERSON_ALL, ...[...apiEmployees].sort((a, b) => a.name.localeCompare(b.name))]}
					value={selPeople}
					onChange={(_, newValue) => {
						if (newValue.some((v) => v.id === PERSON_ALL.id)) {
							setSelPeople(selPeople.length === apiEmployees.length ? [] : [...apiEmployees]);
						} else {
							setSelPeople(newValue);
						}
					}}
					inputValue={peopleInput}
					onInputChange={(_, v) => setPeopleInput(v)}
					getOptionLabel={(opt) => opt.name}
					isOptionEqualToValue={(opt, val) => opt.id === val.id}
					filterOptions={(_, { inputValue }) => {
						const q = inputValue.toLowerCase();
						return [PERSON_ALL, ...apiEmployees.filter((o) => o.name.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name))];
					}}
					renderOption={(props, option, { selected }) => {
						if (option.id === PERSON_ALL.id) {
							const allSel = selPeople.length === apiEmployees.length;
							return (
								<li {...props} key={PERSON_ALL.id}>
									<Checkbox indeterminate={selPeople.length > 0 && !allSel} checked={allSel} size="small" sx={{ mr: 1 }} />
									<span style={{ fontSize: 13, fontWeight: 500 }}>Select All</span>
								</li>
							);
						}
						return (
							<li {...props}>
								<Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={selected} size="small" sx={{ mr: 1 }} />
								<div>
									<div style={{ fontSize: 13 }}>{option.name}</div>
									<div style={{ fontSize: 11, color: "#7a8190" }}>{option.office} · {option.job_title}</div>
								</div>
							</li>
						);
					}}
					renderTags={(value) => peopleInput ? null : (
						<span style={{ fontSize: 13, paddingLeft: 2, whiteSpace: "nowrap", pointerEvents: "none" }}>
							{value.length === apiEmployees.length ? "All People" : `${value.length} selected`}
						</span>
					)}
					renderInput={(params) => <TextField {...params} placeholder={selPeople.length === 0 ? "People" : ""} />}
					sx={{ width: 250, "& .MuiInputBase-root": { flexWrap: "nowrap" }, "& .MuiAutocomplete-input": { minWidth: "0 !important" } }}
				/>}

				{/* Entry type filter */}
				<Autocomplete
					multiple disableCloseOnSelect size="small"
					options={[TYPE_ALL, ...ALL_TYPE_IDS]}
					value={selTypes}
					onChange={(_, newValue) => {
						if (newValue.includes(TYPE_ALL)) {
							setSelTypes(selTypes.length === ALL_TYPE_IDS.length ? [] : ALL_TYPE_IDS);
						} else {
							setSelTypes(newValue);
						}
					}}
					inputValue={typeInput}
					onInputChange={(_, v) => setTypeInput(v)}
					getOptionLabel={(opt) => {
						if (opt === TYPE_ALL) return "Select All";
						return ENTRY_TYPES.find((t) => t.id === opt)?.label ?? opt;
					}}
					filterOptions={(_, { inputValue }) => {
						const q = inputValue.toLowerCase();
						return [TYPE_ALL, ...ENTRY_TYPES.filter((t) => t.label.toLowerCase().includes(q)).map((t) => t.id)];
					}}
					renderOption={(props, option, { selected }) => {
						if (option === TYPE_ALL) {
							const allSel = selTypes.length === ALL_TYPE_IDS.length;
							return (
								<li {...props} key={TYPE_ALL}>
									<Checkbox indeterminate={selTypes.length > 0 && !allSel} checked={allSel} size="small" sx={{ mr: 1 }} />
									<span style={{ fontSize: 13, fontWeight: 500 }}>Select All</span>
								</li>
							);
						}
						const type = ENTRY_TYPES.find((t) => t.id === option);
						return (
							<li {...props}>
								<Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={selected} size="small" sx={{ mr: 1 }} />
								<span style={{ display: "inline-block", width: 12, height: 12, background: type?.color, borderRadius: 2, marginRight: 6, flexShrink: 0 }} />
								<span style={{ fontSize: 13 }}>{type?.label}</span>
							</li>
						);
					}}
					renderTags={(value) => typeInput ? null : (
						<span style={{ fontSize: 13, paddingLeft: 2, whiteSpace: "nowrap", pointerEvents: "none" }}>
							{value.length === ALL_TYPE_IDS.length ? "All Types" : `${value.length} of ${ALL_TYPE_IDS.length}`}
						</span>
					)}
					renderInput={(params) => <TextField {...params} placeholder={selTypes.length === 0 ? "Entry Types" : ""} />}
					sx={{ width: 200, "& .MuiInputBase-root": { flexWrap: "nowrap" }, "& .MuiAutocomplete-input": { minWidth: "0 !important" } }}
				/>

				{/* Date range */}
				<TextField type="date" size="small" value={fromStr} onChange={(e) => e.target.value && setFromStr(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
				<span style={{ color: "#7a8190", fontSize: 13 }}>–</span>
				<TextField type="date" size="small" value={toStr}   onChange={(e) => e.target.value && setToStr(e.target.value)}   InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
				<FormControlLabel
					control={<Switch size="small" checked={showClosedProjects} onChange={(_, v) => setShowClosedProjects(v)} />}
					label="Show inactive projects"
					slotProps={{ typography: { fontSize: 13, color: "#3a4051" } }}
					sx={{ ml: 0.5, mr: 0, gap: 0 }}
				/>
				<Tooltip
					arrow
					placement="top"
					title="Inactive means a project marked Completed, Inactive, or Canceled in BQE. Active and On Hold projects are always shown."
				>
					<InfoOutlinedIcon style={{ fontSize: 16, color: "#8a90a0", cursor: "help" }} />
				</Tooltip>

				<div style={{ flex: 1 }} />

				<Button variant="outlined" size="small" onClick={jumpToToday}>Jump to Today</Button>
			</div>

			{/* ── Chart ────────────────────────────────────────────────────── */}
			<div ref={chartRef} className="wvChart">

				{error ? (
					<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#c62828", fontSize: 13 }}>
						{error}
					</div>
				) : !(employeesLoaded && adminJobTitlesLoaded && budgetsLoaded && spentHoursLoaded && entriesLoadedOnce) ? (
					<div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
						{/* Header row */}
						<div style={{ display: "flex", borderBottom: "2px solid #e3e6eb", background: "#f4f6f9", flexShrink: 0 }}>
							<div style={{ width: LEFT_W + DATES_W, flexShrink: 0, display: "flex", alignItems: "center", padding: "0 16px", borderRight: "1px solid #e3e6eb", height: 42 }}>
								<Skeleton variant="text" width={80} height={13} />
							</div>
							<div style={{ flex: 1, display: "flex", overflow: "hidden", height: 42 }}>
								{[1,1,1,1,1,1].map((_, i) => (
									<div key={i} style={{ flex: 1, height: "100%", borderLeft: "1px solid #e3e6eb", display: "flex", alignItems: "flex-end", padding: "0 5px 6px" }}>
										<Skeleton variant="text" width="65%" height={13} />
									</div>
								))}
							</div>
							<div style={{ width: BUDGET_W, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", height: 42 }}>
								<Skeleton variant="rectangular" width={118} height={26} sx={{ borderRadius: 1 }} />
							</div>
						</div>
						{/* Data rows */}
						{[
							{ nameW: 138, subW:  88, barH: 28, bars: [{ l: "4%",  w: "17%" }, { l: "26%", w: "21%" }, { l: "54%", w: "14%" }] },
							{ nameW: 112, subW:  72, barH: 22, bars: [{ l: "9%",  w: "34%" }, { l: "50%", w: "19%" }] },
							{ nameW: 148, subW:  95, barH: 36, bars: [{ l: "3%",  w: "24%" }, { l: "33%", w: "11%" }, { l: "60%", w: "27%" }] },
							{ nameW: 102, subW:  68, barH: 18, bars: [{ l: "15%", w: "42%" }] },
							{ nameW: 130, subW:  82, barH: 30, bars: [{ l: "6%",  w: "19%" }, { l: "31%", w: "28%" }] },
							{ nameW: 120, subW:  78, barH: 24, bars: [{ l: "2%",  w: "31%" }, { l: "40%", w: "16%" }, { l: "63%", w: "22%" }] },
							{ nameW: 144, subW:  90, barH: 26, bars: [{ l: "11%", w: "38%" }, { l: "55%", w: "13%" }] },
							{ nameW: 118, subW:  75, barH: 32, bars: [{ l: "7%",  w: "22%" }, { l: "36%", w: "18%" }, { l: "62%", w: "24%" }] },
							{ nameW: 135, subW:  85, barH: 20, bars: [{ l: "12%", w: "29%" }, { l: "48%", w: "23%" }] },
							{ nameW: 108, subW:  70, barH: 34, bars: [{ l: "5%",  w: "16%" }, { l: "27%", w: "32%" }, { l: "67%", w: "16%" }] },
							{ nameW: 142, subW:  92, barH: 22, bars: [{ l: "10%", w: "44%" }] },
							{ nameW: 116, subW:  74, barH: 30, bars: [{ l: "1%",  w: "20%" }, { l: "28%", w: "15%" }, { l: "51%", w: "29%" }] },
							{ nameW: 128, subW:  80, barH: 26, bars: [{ l: "8%",  w: "37%" }, { l: "52%", w: "18%" }] },
							{ nameW: 105, subW:  67, barH: 18, bars: [{ l: "14%", w: "25%" }, { l: "44%", w: "20%" }, { l: "70%", w: "15%" }] },
							{ nameW: 140, subW:  88, barH: 32, bars: [{ l: "3%",  w: "28%" }, { l: "38%", w: "34%" }] },
						].map((row, i) => (
							<div key={i} style={{ display: "flex", alignItems: "stretch", height: ROW_H, background: i % 2 === 0 ? "#f4f6f9" : "#fff", borderBottom: "1px solid #eef0f3", flexShrink: 0 }}>
								<div style={{ width: LEFT_W + DATES_W, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 16px", borderRight: "1px solid #e3e6eb", gap: 7 }}>
									<Skeleton variant="text" width={row.nameW} height={14} />
									<Skeleton variant="text" width={row.subW}  height={11} />
								</div>
								<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
									{row.bars.map((bar, bi) => (
										<Skeleton key={bi} animation="wave" variant="rectangular"
											style={{ position: "absolute", left: bar.l, width: bar.w, bottom: 7, height: row.barH }}
											sx={{ borderRadius: "2px 2px 0 0" }} />
									))}
								</div>
								<div style={{ width: BUDGET_W, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", padding: "0 10px", gap: 7 }}>
									<Skeleton variant="text" width={78} height={12} />
									<Skeleton variant="text" width={58} height={12} />
								</div>
							</div>
						))}
					</div>
				) : ({ office: filteredOffices, project: filteredProjects, person: filteredEmployees }[viewMode]?.length === 0) ? (
					<div style={{ padding: "40px 24px", color: "#7a8190", fontSize: 13 }}>
						{selOffices.length === 0
							? "Select an office to view workload data."
							: viewMode === "office"
								? "No offices match the current filters."
								: viewMode === "project"
									? "No projects match the current filters."
									: "No employees match the current filters."}
					</div>
				) : (<>

					{/* ── Column header ──────────────────────────────────────── */}
					<div style={{ display: "flex", borderBottom: "2px solid #e3e6eb", background: "#f4f6f9", flexShrink: 0 }}>
						<div style={{ width: LEFT_W + DATES_W, flexShrink: 0, boxSizing: "border-box", padding: "0 16px", height: zoom === "day" ? 50 : zoom === "week" ? 42 : 28, display: "flex", alignItems: "center", fontSize: 11, fontWeight: 600, color: "#7a8190", borderRight: "1px solid #e3e6eb" }}>
							{viewMode === "office" ? "OFFICE" : viewMode === "project" ? "PROJECT" : "EMPLOYEE"}
						</div>
						<div style={{ flex: 1, position: "relative", overflow: "hidden", height: zoom === "day" ? 50 : zoom === "week" ? 42 : 28 }}>
							{/* Year markers — day and week zoom only */}
							{zoom !== "month" && buckets.map((b, i) => {
								const showYear = i === 0 || b.startDate.getFullYear() !== buckets[i - 1]?.startDate.getFullYear();
								if (!showYear) return null;
								const lPx2 = dayToPx(b.startDay);
								const vis2 = Math.max(0, lPx2);
								if (vis2 > trackW) return null;
								return (
									<div key={"yr" + i} style={{ position: "absolute", left: vis2 + 4, top: 3, fontSize: 9, fontWeight: 700, color: "#6b7280", letterSpacing: ".04em", whiteSpace: "nowrap", pointerEvents: "none", zIndex: 5 }}>
										{b.startDate.getFullYear()}
									</div>
								);
							})}
							{buckets.map((b, i) => {
								const lPx      = dayToPx(b.startDay);
								const wPx      = (b.endDay - b.startDay) * pxPerDay;
								const visLeft  = Math.max(0, lPx);
								const visWidth = Math.min(lPx + wPx, trackW) - visLeft;
								if (visWidth <= 0) return null;
								const minLabelW = zoom === "day" ? 20 : 42;
								const showLabel = visWidth >= minLabelW;
								return (
									<div key={i} style={{
										position: "absolute", left: visLeft, width: visWidth,
										top: 0, bottom: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end",
										alignItems: zoom === "day" ? "center" : "flex-start",
										paddingLeft: zoom === "day" ? 0 : 5,
										paddingBottom: zoom === "day" ? 4 : 6,
										overflow: "hidden", boxSizing: "border-box",
										borderLeft: lPx >= 0 ? "1px solid #e3e6eb" : "none",
										background: b.isWeekend ? "rgba(0,0,0,0.025)" : "transparent",
									}}>
										{showLabel && <span style={{ fontSize: 11, fontWeight: 600, color: b.isWeekend ? "#b6bcc7" : "#3a4051", whiteSpace: "nowrap" }}>{b.label}</span>}
										{showLabel && b.sub && <span style={{ fontSize: 9, color: "#b6bcc7" }}>{b.sub}</span>}
									</div>
								);
							})}
							{todayPx >= 0 && todayPx <= trackW && (
								<div style={{ position: "absolute", left: todayPx, bottom: 0, transform: "translateX(-50%)", padding: "1px 5px", background: "#e8eaee", border: "1px solid #b6bcc7", borderRadius: 3, fontSize: 10, fontWeight: 600, color: "#3a4051", whiteSpace: "nowrap", zIndex: 4 }}>
									Today
								</div>
							)}
						</div>
						<div style={{ width: BUDGET_W, flexShrink: 0, height: zoom === "day" ? 50 : zoom === "week" ? 42 : 28, position: "relative" }}>
							<div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
								<ToggleButtonGroup value={zoom} exclusive size="small" onChange={(_, v) => v && setZoom(v)}
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 12, py: "2px", px: "10px" } }}>
									<ToggleButton value="day">Day</ToggleButton>
									<ToggleButton value="week">Week</ToggleButton>
									<ToggleButton value="month">Month</ToggleButton>
								</ToggleButtonGroup>
							</div>
						</div>
					</div>

					{/* ── Rows ──────────────────────────────────────────────────── */}
					<div style={{ position: "relative", flex: 1, overflowY: "auto", minHeight: 0 }}>

						{viewMode === "office" ? (() => {
							const allOfficeEmployees = filteredOffices.flatMap(o => o.employees);
							const totalAggs = buckets.map(b => aggregateOffice(allOfficeEmployees, b.startDate, b.endDate));
							const totalPlanAggs = planBuckets
								? planBuckets.map(b => aggregateOffice(allOfficeEmployees, b.startDate, b.endDate))
								: null;
							return (<>
							{filteredOffices.map((offRec, offIdx) => {
							const officeAggs = buckets.map(b => aggregateOffice(offRec.employees, b.startDate, b.endDate));
							const officePlanAggs = planBuckets
								? planBuckets.map(b => aggregateOffice(offRec.employees, b.startDate, b.endDate))
								: null;
							return (
							<div key={offRec.office} style={{ display: "flex", alignItems: "stretch", height: ROW_H, background: offIdx % 2 === 0 ? "#f4f6f9" : "#fff", borderBottom: "1px solid #eef0f3" }}>
								{/* Office name / employee count */}
								<div style={{ width: LEFT_W + DATES_W, flexShrink: 0, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 8px 0 16px", gap: 4, borderRight: "1px solid #e3e6eb" }}>
									<div style={{ flex: 1, minWidth: 0 }}>
										<div style={{ fontSize: 13, fontWeight: 600, color: "#1a2540", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{offRec.office}</div>
										<div style={{ fontSize: 11, color: "#7a8190", marginTop: 2 }}>{offRec.employees.length} employee{offRec.employees.length !== 1 ? "s" : ""}</div>
									</div>
								</div>
								{/* Timeline track */}
								<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
									{showPlanned && (zoom === "day" ? (planBuckets || []) : buckets).map((b, bi) => {
										if (b.startDate < TODAY) return null;
										const lPx = dayToPx(b.startDay);
										const wPx = zoom === "day" ? 5 * pxPerDay : (b.endDay - b.startDay) * pxPerDay;
										if (lPx + wPx < 0 || lPx > trackW) return null;
										const agg = zoom === "day" ? officePlanAggs[bi] : officeAggs[bi];
										const us = getUtilStyle(agg.futurePlan, agg.billCap);
										if (!us) return null;
										const pct = Math.max(1, Math.round((agg.futurePlan / agg.billCap) * 100));
										const cx = lPx + wPx / 2;
										return (
											<Tooltip key={"ut" + bi} arrow placement="top" title={`${agg.futurePlan.toFixed(1)}h projected of ${agg.billCap.toFixed(0)}h capacity`}>
												<div style={{ position: "absolute", left: cx, top: 6, transform: "translateX(-50%)", padding: "1px 6px", background: us.bg, border: `1px solid ${us.border}`, borderRadius: 8, fontSize: 12, fontWeight: 700, color: us.text, whiteSpace: "nowrap", zIndex: 3, fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
													{fmtH(agg.futurePlan)}h / {pct}%
												</div>
											</Tooltip>
										);
									})}
									{buckets.map((b, bi) => {
										const lPx = dayToPx(b.startDay);
										if (lPx < 0 || lPx > trackW) return null;
										return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #e3e6eb", pointerEvents: "none" }} />;
									})}
									{todayPx >= 0 && todayPx <= trackW && (
										<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.6, pointerEvents: "none", zIndex: 3 }} />
									)}
									{buckets.map((b, bi) => {
										const lPx = dayToPx(b.startDay);
										const wPx = (b.endDay - b.startDay) * pxPerDay;
										if (lPx + wPx < 0 || lPx > trackW) return null;
										const agg = officeAggs[bi];
										const grossRef = agg.capacity + agg.offH + agg.holidayH + agg.bill + agg.nonbill;
										if (grossRef === 0) return null;
										const visContent =
											(showBillable ? agg.bill       : 0) +
											(showNonbill  ? agg.nonbill    : 0) +
											(showPlanned && zoom !== "day" ? agg.futurePlan : 0) +
											(showTimeOff  ? agg.offH       : 0) +
											(showHolidays ? agg.holidayH   : 0);
										const tH = tieredBarH(visContent);
										if (tH === 0) return null;
										const scale    = tH / visContent;
										const billH    = showBillable ? Math.round(agg.bill    * scale) : 0;
										const nbH      = showNonbill  ? Math.round(agg.nonbill * scale) : 0;
										const planBarH = showPlanned && zoom !== "day" && agg.futurePlan > 0 ? Math.max(1, Math.round(agg.futurePlan * scale)) : 0;
										const offBarH  = showTimeOff  ? Math.round(agg.offH    * scale) : 0;
										const holH     = showHolidays ? Math.round(agg.holidayH * scale) : 0;
										const segs = [
											holH     > 0 && { h: holH,     bg: "#f59e0b" },
											offBarH  > 0 && { h: offBarH,  bg: "#9575cd" },
											planBarH > 0 && { h: planBarH, bg: "#5b8dd6", hatch: true },
											nbH      > 0 && { h: nbH,      bg: "#ef5350" },
											billH    > 0 && { h: billH,    bg: "#43a047" },
										].filter(Boolean);
										if (segs.length === 0) return null;
										const tip = (
											<div style={{ fontSize: 12 }}>
												<div style={{ fontWeight: 700, marginBottom: 3 }}>{offRec.office}</div>
												<div style={{ color: "#aaa", marginBottom: 3, fontSize: 11 }}>{offRec.employees.length} employees</div>
												<div style={{ color: "#ccc", marginBottom: 3 }}>{b.label}</div>
												{agg.bill       > 0 && <div>Billable: <strong>{agg.bill.toFixed(1)}h</strong></div>}
												{agg.nonbill    > 0 && <div>Non-billable: <strong>{agg.nonbill.toFixed(1)}h</strong></div>}
												{agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Planned: <strong>{agg.futurePlan.toFixed(1)}h</strong></div>}
												{agg.offDays    > 0 && <div style={{ color: "#ce93d8" }}>Off: <strong>{agg.offDays} day{agg.offDays > 1 ? "s" : ""}</strong></div>}
												{agg.holidayH   > 0 && <div style={{ color: "#fbbf24" }}>Holiday: <strong>{agg.holidayH.toFixed(0)}h</strong></div>}
												{agg.bill > 0 && <div>Billable %: <strong>{Math.round(agg.bill / (agg.bill + (agg.nonbill || 0)) * 100)}%</strong></div>}
												{agg.billCap > 0 && agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{Math.max(1, Math.round(agg.futurePlan / agg.billCap * 100))}%</strong></div>}
											</div>
										);
										return (
											<Tooltip key={bi} arrow placement="top" title={tip}>
												<div style={{ position: "absolute", left: lPx + 2, width: Math.max(2, wPx - 4), bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 2 }}>
													{segs.map((seg, si) => (
														<div key={si} style={{ width: "100%", height: seg.h, background: seg.bg, backgroundImage: seg.hatch ? PLAN_HATCH : undefined, borderRadius: si === 0 ? (segs.length === 1 ? 3 : "3px 3px 0 0") : (si === segs.length - 1 ? "0 0 3px 3px" : 0) }} />
													))}
												</div>
											</Tooltip>
										);
									})}
									{showPlanned && zoom === "day" && planBuckets && planBuckets.map((b, bi) => {
										const lPx = dayToPx(b.startDay);
										const wPx = 5 * pxPerDay;
										if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
										const agg = officePlanAggs[bi];
										if (agg.futurePlan < 0.05) return null;
										const barH = tieredBarH(agg.futurePlan);
										const tip = (
											<div style={{ fontSize: 12 }}>
												<div style={{ fontWeight: 700, marginBottom: 3 }}>{offRec.office}</div>
												<div style={{ color: "#ccc", marginBottom: 3 }}>{b.label}</div>
												<div style={{ color: "#90b4e8" }}>Planned: <strong>{agg.futurePlan.toFixed(1)}h</strong></div>
												{agg.billCap > 0 && <div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{Math.max(1, Math.round(agg.futurePlan / agg.billCap * 100))}%</strong></div>}
											</div>
										);
										return (
											<Tooltip key={"pw" + bi} arrow placement="top" title={tip}>
												<div style={{ position: "absolute", left: lPx + 2, width: wPx - 4, bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 1 }}>
													<div style={{ width: "100%", height: barH, background: "#5b8dd6", backgroundImage: PLAN_HATCH, borderRadius: "3px 3px 0 0" }} />
												</div>
											</Tooltip>
										);
									})}
								</div>
								{/* Budget column spacer */}
								<div style={{ width: BUDGET_W, flexShrink: 0 }} />
							</div>
						); })}
						{/* ── Company Total row ── */}
						<div style={{ display: "flex", alignItems: "stretch", height: ROW_H, background: "#dce3f0", borderTop: "2px solid #b0bbd0" }}>
							<div style={{ width: LEFT_W + DATES_W, flexShrink: 0, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 8px 0 16px", gap: 4, borderRight: "1px solid #c5cfe0" }}>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontSize: 13, fontWeight: 700, color: "#1a2540", lineHeight: 1.3 }}>Company Total</div>
									<div style={{ fontSize: 11, color: "#5a6478", marginTop: 2 }}>{allOfficeEmployees.length} employee{allOfficeEmployees.length !== 1 ? "s" : ""}</div>
								</div>
							</div>
							<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
								{showPlanned && (zoom === "day" ? (planBuckets || []) : buckets).map((b, bi) => {
									if (b.startDate < TODAY) return null;
									const lPx = dayToPx(b.startDay);
									const wPx = zoom === "day" ? 5 * pxPerDay : (b.endDay - b.startDay) * pxPerDay;
									if (lPx + wPx < 0 || lPx > trackW) return null;
									const agg = zoom === "day" ? totalPlanAggs[bi] : totalAggs[bi];
									const us = getUtilStyle(agg.futurePlan, agg.billCap);
									if (!us) return null;
									const pct = Math.max(1, Math.round((agg.futurePlan / agg.billCap) * 100));
									const cx = lPx + wPx / 2;
									return (
										<Tooltip key={"ut" + bi} arrow placement="top" title={`${agg.futurePlan.toFixed(1)}h projected of ${agg.billCap.toFixed(0)}h capacity`}>
											<div style={{ position: "absolute", left: cx, top: 6, transform: "translateX(-50%)", padding: "1px 6px", background: us.bg, border: `1px solid ${us.border}`, borderRadius: 8, fontSize: 12, fontWeight: 700, color: us.text, whiteSpace: "nowrap", zIndex: 3, fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
												{fmtH(agg.futurePlan)}h / {pct}%
											</div>
										</Tooltip>
									);
								})}
								{buckets.map((b, bi) => {
									const lPx = dayToPx(b.startDay);
									if (lPx < 0 || lPx > trackW) return null;
									return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #c5cfe0", pointerEvents: "none" }} />;
								})}
								{todayPx >= 0 && todayPx <= trackW && (
									<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.6, pointerEvents: "none", zIndex: 3 }} />
								)}
								{buckets.map((b, bi) => {
									const lPx = dayToPx(b.startDay);
									const wPx = (b.endDay - b.startDay) * pxPerDay;
									if (lPx + wPx < 0 || lPx > trackW) return null;
									const agg = totalAggs[bi];
									const grossRef = agg.capacity + agg.offH + agg.holidayH + agg.bill + agg.nonbill;
									if (grossRef === 0) return null;
									const visContent =
										(showBillable ? agg.bill       : 0) +
										(showNonbill  ? agg.nonbill    : 0) +
										(showPlanned && zoom !== "day" ? agg.futurePlan : 0) +
										(showTimeOff  ? agg.offH       : 0) +
										(showHolidays ? agg.holidayH   : 0);
									const tH = tieredBarH(visContent);
									if (tH === 0) return null;
									const scale    = tH / visContent;
									const billH    = showBillable ? Math.round(agg.bill    * scale) : 0;
									const nbH      = showNonbill  ? Math.round(agg.nonbill * scale) : 0;
									const planBarH = showPlanned && zoom !== "day" && agg.futurePlan > 0 ? Math.max(1, Math.round(agg.futurePlan * scale)) : 0;
									const offBarH  = showTimeOff  ? Math.round(agg.offH    * scale) : 0;
									const holH     = showHolidays ? Math.round(agg.holidayH * scale) : 0;
									const segs = [
										holH     > 0 && { h: holH,     bg: "#f59e0b" },
										offBarH  > 0 && { h: offBarH,  bg: "#9575cd" },
										planBarH > 0 && { h: planBarH, bg: "#5b8dd6", hatch: true },
										nbH      > 0 && { h: nbH,      bg: "#ef5350" },
										billH    > 0 && { h: billH,    bg: "#43a047" },
									].filter(Boolean);
									if (segs.length === 0) return null;
									const tip = (
										<div style={{ fontSize: 12 }}>
											<div style={{ fontWeight: 700, marginBottom: 3 }}>Company Total</div>
											<div style={{ color: "#aaa", marginBottom: 3, fontSize: 11 }}>{allOfficeEmployees.length} employees</div>
											<div style={{ color: "#ccc", marginBottom: 3 }}>{b.label}</div>
											{agg.bill       > 0 && <div>Billable: <strong>{agg.bill.toFixed(1)}h</strong></div>}
											{agg.nonbill    > 0 && <div>Non-billable: <strong>{agg.nonbill.toFixed(1)}h</strong></div>}
											{agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Planned: <strong>{agg.futurePlan.toFixed(1)}h</strong></div>}
											{agg.offDays    > 0 && <div style={{ color: "#ce93d8" }}>Off: <strong>{agg.offDays} day{agg.offDays > 1 ? "s" : ""}</strong></div>}
											{agg.holidayH   > 0 && <div style={{ color: "#fbbf24" }}>Holiday: <strong>{agg.holidayH.toFixed(0)}h</strong></div>}
											{agg.bill > 0 && <div>Billable %: <strong>{Math.round(agg.bill / (agg.bill + (agg.nonbill || 0)) * 100)}%</strong></div>}
											{agg.billCap > 0 && agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{Math.max(1, Math.round(agg.futurePlan / agg.billCap * 100))}%</strong></div>}
										</div>
									);
									return (
										<Tooltip key={bi} arrow placement="top" title={tip}>
											<div style={{ position: "absolute", left: lPx + 2, width: Math.max(2, wPx - 4), bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 2 }}>
												{segs.map((seg, si) => (
													<div key={si} style={{ width: "100%", height: seg.h, background: seg.bg, backgroundImage: seg.hatch ? PLAN_HATCH : undefined, borderRadius: si === 0 ? (segs.length === 1 ? 3 : "3px 3px 0 0") : (si === segs.length - 1 ? "0 0 3px 3px" : 0) }} />
												))}
											</div>
										</Tooltip>
									);
								})}
								{showPlanned && zoom === "day" && planBuckets && totalPlanAggs && planBuckets.map((b, bi) => {
									const lPx = dayToPx(b.startDay);
									const wPx = 5 * pxPerDay;
									if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
									const agg = totalPlanAggs[bi];
									if (agg.futurePlan < 0.05) return null;
									const barH = tieredBarH(agg.futurePlan);
									return (
										<Tooltip key={"pw" + bi} arrow placement="top" title={`Company Total: ${fmtH(agg.futurePlan)}h planned`}>
											<div style={{ position: "absolute", left: lPx + 2, width: wPx - 4, bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 1 }}>
												<div style={{ width: "100%", height: barH, background: "#5b8dd6", backgroundImage: PLAN_HATCH, borderRadius: "3px 3px 0 0" }} />
											</div>
										</Tooltip>
									);
								})}
							</div>
							<div style={{ width: BUDGET_W, flexShrink: 0 }} />
						</div>
						</>);
						})() : viewMode === "project" ? filteredProjects.map(({ proj, employees }, projIdx) => {
							const isExp = expanded.has(proj.id);
							const projAggs = buckets.map(b => aggregateProject(employees, proj.id, b.startDate, b.endDate));
							const projEndPx = proj.endDate ? dayToPx(daysBetween(fromDate, proj.endDate) + 1) : null;
							const clipW = (lPx, fullW) => projEndPx !== null ? Math.max(0, Math.min(fullW, projEndPx - lPx)) : fullW;
							const totalBudgetH = budgetByProject.get(proj.id) ?? null;
							const totalSpentH  = spentByProject.get(proj.id)  || 0;
							const totalRemainH = totalBudgetH !== null ? totalBudgetH - totalSpentH : null;
							return (
								<React.Fragment key={proj.id}>
									{/* ── Project main row ─── */}
									<div style={{ display: "flex", alignItems: "stretch", height: ROW_H, background: projIdx % 2 === 0 ? "#f4f6f9" : "#fff", borderBottom: "1px solid #eef0f3", opacity: proj.status === "other" ? 0.7 : 1 }}>
										<div onClick={employees.length > 0 ? () => toggleExpand(proj.id) : undefined} style={{ width: LEFT_W, flexShrink: 0, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 8px 0 16px", cursor: employees.length > 0 ? "pointer" : "default", gap: 4 }}>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
													<a href={`/projects/viewer?recordId=${encodeURIComponent(proj.id)}&tab=schedule`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="wvProjectLink" style={{ fontSize: 13, fontWeight: 600 }}>{proj.name}</a>
													{renderStatusChip(proj.status)}
												</div>
												{(proj.projectName || proj.phaseName) && (
													<div style={{ fontSize: 11, color: "#7a8190", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
														{[proj.projectName, proj.phaseName].filter(Boolean).join(" · ")}
													</div>
												)}
											</div>
											{employees.length > 0 && (isExp ? <ExpandLessIcon style={{ fontSize: 16, color: "#5a6478", flexShrink: 0 }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: "#5a6478", flexShrink: 0 }} />)}
										</div>
										{(() => {
											const pb = apiBudgets.find(b => b.project_id === proj.id);
											const durWks = pb?.duration_weeks;
											const durStr = durWks != null ? `${durWks} wk` : null;
											return (
												<div style={{ width: DATES_W, flexShrink: 0, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", fontSize: 11, lineHeight: 1.4, gap: 4, color: "#3a4051", borderRight: "1px solid #e3e6eb", padding: "0 8px" }}>
													<div>Start — {proj.startDate ? <strong>{fmtShort(proj.startDate)}</strong> : <span style={{ color: "#c62828" }}>missing</span>}</div>
													<div>End — {proj.endDate ? <strong>{fmtShort(proj.endDate)}</strong> : <span style={{ color: "#c62828" }}>missing</span>}</div>
													<div>Dur — {durStr ? <strong>{durStr}</strong> : <span style={{ color: "#c62828" }}>missing</span>}</div>
												</div>
											);
										})()}
										<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
											{buckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												if (lPx < 0 || lPx > trackW) return null;
												return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #e3e6eb", pointerEvents: "none" }} />;
											})}
											{todayPx >= 0 && todayPx <= trackW && (
												<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.6, pointerEvents: "none", zIndex: 3 }} />
											)}
											{proj.startDate && (() => {
												const px = dayToPx(daysBetween(fromDate, proj.startDate));
												if (px < -1 || px > trackW) return null;
												return (<React.Fragment>
													<div style={{ position: "absolute", left: px, top: 0, bottom: 0, borderLeft: "2px solid #4ea861", opacity: 0.55, pointerEvents: "none", zIndex: 2 }} />
													<div style={{ position: "absolute", left: px + 3, top: 3, padding: "1px 4px", background: "#dcfce7", border: "1px solid #4ea861", borderRadius: 3, fontSize: 10, fontWeight: 600, color: "#14532d", whiteSpace: "nowrap", zIndex: 4, pointerEvents: "none" }}>
														{fmtShort(proj.startDate)}
													</div>
												</React.Fragment>);
											})()}
											{proj.endDate && (() => {
												const px = dayToPx(daysBetween(fromDate, proj.endDate) + 1);
												if (px < 0 || px > trackW + 1) return null;
												return (<React.Fragment>
													<div style={{ position: "absolute", left: px, top: 0, bottom: 0, borderLeft: "2px solid #c62828", opacity: 0.55, pointerEvents: "none", zIndex: 2 }} />
													<div style={{ position: "absolute", left: px - 3, top: 3, transform: "translateX(-100%)", padding: "1px 4px", background: "#fee2e2", border: "1px solid #c62828", borderRadius: 3, fontSize: 10, fontWeight: 600, color: "#7f1d1d", whiteSpace: "nowrap", zIndex: 4, pointerEvents: "none" }}>
														{fmtShort(proj.endDate)}
													</div>
												</React.Fragment>);
											})()}
											{renderOnHoldBand(proj)}
											{buckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												const wPx = clipW(lPx, (b.endDay - b.startDay) * pxPerDay);
												if (lPx + wPx < 0 || lPx > trackW || wPx <= 0) return null;
												const agg = projAggs[bi];
												const visContent =
													(showBillable ? agg.bill       : 0) +
													(showPlanned && zoom !== "day" ? agg.futurePlan : 0);
												const tH = tieredBarH(visContent);
												if (tH === 0) return null;
												const scale    = tH / visContent;
												const billH    = showBillable ? Math.round(agg.bill * scale) : 0;
												const planBarH = showPlanned && zoom !== "day" && agg.futurePlan > 0 ? Math.max(1, Math.round(agg.futurePlan * scale)) : 0;
												const segs = [
													planBarH > 0 && { h: planBarH, bg: "#5b8dd6", hatch: true },
													billH    > 0 && { h: billH,    bg: "#43a047" },
												].filter(Boolean);
												if (segs.length === 0) return null;
												const tip = (
													<div style={{ fontSize: 12 }}>
														<div style={{ fontWeight: 700, marginBottom: 3 }}>{proj.name}</div>
														<div style={{ color: "#aaa", marginBottom: 3, fontSize: 11 }}>{employees.length} person{employees.length !== 1 ? "s" : ""}</div>
														<div style={{ color: "#ccc", marginBottom: 3 }}>{b.label}</div>
														{agg.bill       > 0 && <div>Billable: <strong>{agg.bill.toFixed(1)}h</strong></div>}
														{agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Planned: <strong>{agg.futurePlan.toFixed(1)}h</strong></div>}
													</div>
												);
												return (
													<Tooltip key={bi} arrow placement="top" title={tip}>
														<div style={{ position: "absolute", left: lPx + 2, width: Math.max(2, wPx - 4), bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 2 }}>
															{segs.map((seg, si) => (
																<div key={si} style={{ width: "100%", height: seg.h, background: seg.bg, backgroundImage: seg.hatch ? PLAN_HATCH : undefined, borderRadius: si === 0 ? (segs.length === 1 ? 3 : "3px 3px 0 0") : (si === segs.length - 1 ? "0 0 3px 3px" : 0) }} />
															))}
														</div>
													</Tooltip>
												);
											})}
											{showPlanned && zoom === "day" && planBuckets && planBuckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												const wPx = clipW(lPx, 5 * pxPerDay);
												if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
												const agg = aggregateProject(employees, proj.id, b.startDate, b.endDate);
												if (agg.futurePlan < 0.05) return null;
												const barH = tieredBarH(agg.futurePlan);
												return (
													<Tooltip key={"pw" + bi} arrow placement="top" title={`${proj.name}: ${fmtH(agg.futurePlan)}h planned`}>
														<div style={{ position: "absolute", left: lPx + 2, width: wPx - 4, bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 1 }}>
															<div style={{ width: "100%", height: barH, background: "#5b8dd6", backgroundImage: PLAN_HATCH, borderRadius: "3px 3px 0 0" }} />
														</div>
													</Tooltip>
												);
											})}
										</div>
										<div style={{ width: BUDGET_W, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", paddingRight: 10, fontSize: 11, lineHeight: 1.4, gap: 4, color: "#3a4051" }}>
											<div>Spent — <strong>{fmtH(totalSpentH)}</strong>h</div>
											{totalBudgetH !== null && (<>
												<div style={totalRemainH < 0 ? { color: "#c62828" } : undefined}>
													Remaining — <strong>{fmtH(totalRemainH)}</strong>h
												</div>
												<div>Budget — <strong>{fmtH(totalBudgetH)}</strong>h</div>
											</>)}
										</div>
									</div>

									{/* ── Employee sub-rows ─── */}
									{isExp && employees.map((emp) => {
										const relationshipLabel = (() => {
											const managerName = normalizeName(proj.manager);
											if (managerName) {
												return [emp.name, emp.bqeName].some((name) => isPersonNameMatch(name, managerName))
													? "Owner"
													: "Contributor";
											}
											return proj.manager_bqe_id && proj.manager_bqe_id === emp.bqeId
												? "Owner"
												: "Contributor";
										})();
										const empBudgets = apiBudgets.filter(b => {
											if (b.project_id !== proj.id) return false;
											if (emp.bqeId && b.resource_id) return String(b.resource_id) === emp.bqeId;
											return normalizeName(b.employee) === normalizeName(emp.name);
										});
										const ridKey  = emp.bqeId ? `${emp.bqeId}|${proj.id}` : null;
										const spentH  = (ridKey && spentByResourceId.has(ridKey))
											? spentByResourceId.get(ridKey)
											: (spentByName.get(`${normalizeName(emp.name)}|${proj.id}`) || 0);
										const budgetH = empBudgets.length > 0 ? empBudgets.reduce((s, b) => s + (b.budget_hours || 0), 0) : null;
										const remainH = budgetH !== null ? budgetH - spentH : null;
										return (
											<div key={emp.id} style={{ display: "flex", alignItems: "stretch", height: SUB_ROW_H, background: "#eaecf4", borderBottom: "1px solid #c8cdd8", borderLeft: "3px solid #b0b8d0" }}>
												<div style={{ width: LEFT_W - 3, flexShrink: 0, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 8px 0 16px", minWidth: 0, gap: 2 }}>
													<div style={{ fontSize: 12, fontWeight: 600, color: "#1a2540", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.name}</div>
													<div style={{ fontSize: 11, color: "#7a8190" }}>{emp.role}</div>
													<div style={{ fontSize: 11, color: "#586174", fontWeight: 600 }}>{relationshipLabel}</div>
												</div>
												<div style={{ width: DATES_W, flexShrink: 0, boxSizing: "border-box", borderRight: "1px solid #c8cdd8" }} />
												<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
													{buckets.map((b, bi) => {
														const lPx = dayToPx(b.startDay);
														if (lPx < 0 || lPx > trackW) return null;
														return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #e3e6eb", pointerEvents: "none" }} />;
													})}
													{todayPx >= 0 && todayPx <= trackW && (
														<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.4, pointerEvents: "none", zIndex: 3 }} />
													)}
													{buckets.map((b, bi) => {
														const lPx  = dayToPx(b.startDay);
														const wPx  = (b.endDay - b.startDay) * pxPerDay;
														if (lPx + wPx < 0 || lPx > trackW) return null;
														const agg   = aggregateRange(emp, b.startDate, b.endDate);
														const billH = agg.projBill[proj.id] || 0;
														const nbH   = agg.projNonbill[proj.id] || 0;
														const planH = zoom !== "day" ? (agg.projPlan[proj.id] || 0) : 0;
														const showBillChip = showBillable && billH >= 0.05;
														const showNbChip   = showNonbill  && nbH  >= 0.05;
														const showPlanChip = showPlanned  && planH >= 0.05;
														if (!showBillChip && !showNbChip && !showPlanChip) return null;
														const cx = lPx + wPx / 2;
														return (
															<div key={bi} style={{ position: "absolute", left: cx, top: "50%", transform: "translate(-50%, -50%)", display: "flex", gap: 3, alignItems: "center", zIndex: 2 }}>
																{showBillChip && (
																	<Tooltip arrow placement="top" title={`${emp.name}: ${fmtH(billH)}h billable`}>
																		<div style={{ padding: "2px 7px", borderRadius: 6, background: "#b9f0c0", border: "1.5px solid #4ea861", color: "#1a3d23", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																			{fmtH(billH)}
																		</div>
																	</Tooltip>
																)}
																{showNbChip && (
																	<Tooltip arrow placement="top" title={`${emp.name}: ${fmtH(nbH)}h non-billable`}>
																		<div style={{ padding: "2px 7px", borderRadius: 6, background: "#fecaca", border: "1.5px solid #ef5350", color: "#7f1d1d", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																			{fmtH(nbH)}
																		</div>
																	</Tooltip>
																)}
																{showPlanChip && (
																	<Tooltip arrow placement="top" title={`${emp.name}: ${fmtH(planH)}h planned`}>
																		<div style={{ padding: "2px 7px", borderRadius: 6, background: "#bcd8ff", border: "1.5px solid #5b8dd6", color: "#19345e", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																			{fmtH(planH)}
																		</div>
																	</Tooltip>
																)}
															</div>
														);
													})}
													{showPlanned && zoom === "day" && planBuckets && planBuckets.map((b, bi) => {
														const lPx  = dayToPx(b.startDay);
														const wPx  = clipW(lPx, 5 * pxPerDay);
														if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
														const agg   = aggregateRange(emp, b.startDate, b.endDate);
														const planH = agg.projPlan[proj.id] || 0;
														if (planH < 0.05) return null;
														return (
															<Tooltip key={"pw" + bi} arrow placement="top" title={`${emp.name}: ${fmtH(planH)}h planned`}>
																<div style={{ position: "absolute", left: lPx + 4, width: wPx - 8, height: 20, top: "50%", transform: "translateY(-50%)", background: "#bcd8ff", backgroundImage: PLAN_HATCH, border: "1.5px solid #5b8dd6", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: "#19345e", overflow: "hidden", zIndex: 1, fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																	{fmtH(planH)}
																</div>
															</Tooltip>
														);
													})}
												</div>
												<div style={{ width: BUDGET_W, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", paddingRight: 10, fontSize: 11, lineHeight: 1.4, gap: 4, color: "#3a4051" }}>
													<div>Spent — <strong>{fmtH(spentH)}</strong>h</div>
													{budgetH !== null && (<>
														<div style={remainH < 0 ? { color: "#c62828" } : undefined}>
															Remaining — <strong>{fmtH(remainH)}</strong>h
														</div>
														<div>Budget — <strong>{fmtH(budgetH)}</strong>h</div>
													</>)}
												</div>
											</div>
										);
									})}
								</React.Fragment>
							);
						}) : filteredEmployees.map((emp, empIdx) => {
							const isExp = expanded.has(emp.id);
							return (
								<React.Fragment key={emp.id}>

									{/* Main row */}
									<div style={{ display: "flex", alignItems: "stretch", height: ROW_H, background: empIdx % 2 === 0 ? "#f4f6f9" : "#fff", borderBottom: "1px solid #eef0f3" }}>

										{/* Name / role */}
										<div onClick={emp.projects.length > 0 ? () => toggleExpand(emp.id) : undefined} style={{ width: LEFT_W + DATES_W, flexShrink: 0, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 8px 0 16px", cursor: emp.projects.length > 0 ? "pointer" : "default", gap: 4, borderRight: "1px solid #e3e6eb" }}>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div style={{ fontSize: 13, fontWeight: 600, color: "#1a2540", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.name}</div>
												<div style={{ fontSize: 11, color: "#7a8190", marginTop: 2 }}>{emp.role} · {emp.office}</div>
											</div>
											{emp.projects.length > 0 && (isExp ? <ExpandLessIcon style={{ fontSize: 16, color: "#5a6478", flexShrink: 0 }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: "#5a6478", flexShrink: 0 }} />)}
										</div>

										{/* Timeline track */}
										<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>

											{/* Utilisation % pills — future buckets only, week granularity in day zoom */}
											{showPlanned && (zoom === "day" ? (planBuckets || []) : buckets).map((b, bi) => {
												if (b.startDate < TODAY) return null;
												const lPx = dayToPx(b.startDay);
												const wPx = zoom === "day" ? 5 * pxPerDay : (b.endDay - b.startDay) * pxPerDay;
												if (lPx + wPx < 0 || lPx > trackW) return null;
												const agg = aggregateRange(emp, b.startDate, b.endDate);
												const us  = getUtilStyle(agg.futurePlan, agg.billCap);
												if (!us) return null;
												const pct = Math.max(1, Math.round((agg.futurePlan / agg.billCap) * 100));
												const cx  = lPx + wPx / 2;
												return (
													<Tooltip key={"ut" + bi} arrow placement="top" title={`${agg.futurePlan.toFixed(1)}h projected of ${agg.billCap.toFixed(0)}h capacity`}>
														<div style={{ position: "absolute", left: cx, top: 6, transform: "translateX(-50%)", padding: "1px 6px", background: us.bg, border: `1px solid ${us.border}`, borderRadius: 8, fontSize: 12, fontWeight: 700, color: us.text, whiteSpace: "nowrap", zIndex: 3, fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
															{fmtH(agg.futurePlan)}h / {pct}%
														</div>
													</Tooltip>
												);
											})}

											{/* Grid lines */}
											{buckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												if (lPx < 0 || lPx > trackW) return null;
												return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #e3e6eb", pointerEvents: "none" }} />;
											})}

											{/* Today line */}
											{todayPx >= 0 && todayPx <= trackW && (
												<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.6, pointerEvents: "none", zIndex: 3 }} />
											)}

											{/* Bars */}
											{buckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												const wPx = (b.endDay - b.startDay) * pxPerDay;
												if (lPx + wPx < 0 || lPx > trackW) return null;

												const agg      = aggregateRange(emp, b.startDate, b.endDate);
												const grossRef = agg.capacity + agg.offH + agg.holidayH + agg.bill + agg.nonbill;
												if (grossRef === 0) return null;

												// Total visible content drives the tier; segments fill proportionally within it.
												const visContent =
													(showBillable ? agg.bill       : 0) +
													(showNonbill  ? agg.nonbill    : 0) +
													(showPlanned && zoom !== "day" ? agg.futurePlan : 0) +
													(showTimeOff  ? agg.offH       : 0) +
													(showHolidays ? agg.holidayH   : 0);
												const tH = tieredBarH(visContent);
												if (tH === 0) return null;
												const scale    = tH / visContent;
												const billH    = showBillable ? Math.round(agg.bill    * scale) : 0;
												const nbH      = showNonbill  ? Math.round(agg.nonbill * scale) : 0;
												const planBarH = showPlanned && zoom !== "day" && agg.futurePlan > 0 ? Math.max(1, Math.round(agg.futurePlan * scale)) : 0;
												const offBarH  = showTimeOff  ? Math.round(agg.offH    * scale) : 0;
												const holH     = showHolidays ? Math.round(agg.holidayH * scale) : 0;

												const segs = [
													holH     > 0 && { h: holH,     bg: "#f59e0b" },
													offBarH  > 0 && { h: offBarH,  bg: "#9575cd" },
													planBarH > 0 && { h: planBarH, bg: "#5b8dd6", hatch: true },
													nbH      > 0 && { h: nbH,      bg: "#ef5350" },
													billH    > 0 && { h: billH,    bg: "#43a047" },
												].filter(Boolean);
												if (segs.length === 0) return null;

												const empTypeLine = [
													emp.employmentType === "full_time" ? "Full Time" : emp.employmentType === "part_time" ? "Part Time" : null,
													emp.hoursPerWeek != null ? `${emp.hoursPerWeek}h/week` : null,
												].filter(Boolean).join(" · ");

												const tip = (
													<div style={{ fontSize: 12 }}>
														<div style={{ fontWeight: 700, marginBottom: empTypeLine ? 1 : 3 }}>{emp.name}</div>
														{empTypeLine && <div style={{ color: "#aaa", marginBottom: 3, fontSize: 11 }}>{empTypeLine}</div>}
														<div style={{ color: "#ccc", marginBottom: 3 }}>{b.label}</div>
														{agg.bill       > 0 && <div>Billable: <strong>{agg.bill.toFixed(1)}h</strong></div>}
														{agg.nonbill    > 0 && <div>Non-billable: <strong>{agg.nonbill.toFixed(1)}h</strong></div>}
														{agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Planned: <strong>{agg.futurePlan.toFixed(1)}h</strong></div>}
														{agg.offDays    > 0 && <div style={{ color: "#ce93d8" }}>Off: <strong>{agg.offDays} day{agg.offDays > 1 ? "s" : ""}</strong></div>}
														{agg.holidayH > 0 && <div style={{ color: "#fbbf24" }}>Holiday: <strong>{agg.holidayH.toFixed(0)}h</strong></div>}
														{agg.bill > 0 && <div>Billable %: <strong>{Math.round(agg.bill / (agg.bill + (agg.nonbill || 0)) * 100)}%</strong></div>}
								{agg.billCap > 0 && agg.futurePlan > 0 && <div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{Math.max(1, Math.round(agg.futurePlan / agg.billCap * 100))}%</strong></div>}
													</div>
												);

												return (
													<Tooltip key={bi} arrow placement="top" title={tip}>
														<div style={{ position: "absolute", left: lPx + 2, width: Math.max(2, wPx - 4), bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 2 }}>
															{segs.map((seg, si) => (
																<div key={si} style={{ width: "100%", height: seg.h, background: seg.bg, backgroundImage: seg.hatch ? PLAN_HATCH : undefined, borderRadius: si === 0 ? (segs.length === 1 ? 3 : "3px 3px 0 0") : (si === segs.length - 1 ? "0 0 3px 3px" : 0) }} />
															))}
														</div>
													</Tooltip>
												);
											})}
										{/* Plan week-bars in day zoom — proportional bar spanning the full week */}
										{showPlanned && zoom === "day" && planBuckets && planBuckets.map((b, bi) => {
											const lPx = dayToPx(b.startDay);
											const wPx = 5 * pxPerDay;
											if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
											const agg = aggregateRange(emp, b.startDate, b.endDate);
											if (agg.futurePlan < 0.05) return null;
											const barH = tieredBarH(agg.futurePlan);
											const tip = (
												<div style={{ fontSize: 12 }}>
													<div style={{ fontWeight: 700, marginBottom: 3 }}>{emp.name}</div>
													<div style={{ color: "#ccc", marginBottom: 3 }}>{b.label}</div>
													<div style={{ color: "#90b4e8" }}>Planned: <strong>{agg.futurePlan.toFixed(1)}h</strong></div>
													{agg.billCap > 0 && <div style={{ color: "#90b4e8" }}>Projected billable %: <strong>{Math.max(1, Math.round(agg.futurePlan / agg.billCap * 100))}%</strong></div>}
												</div>
											);
											return (
												<Tooltip key={"pw" + bi} arrow placement="top" title={tip}>
													<div style={{ position: "absolute", left: lPx + 2, width: wPx - 4, bottom: 7, height: BAR_MAX_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 1 }}>
														<div style={{ width: "100%", height: barH, background: "#5b8dd6", backgroundImage: PLAN_HATCH, borderRadius: "3px 3px 0 0" }} />
													</div>
												</Tooltip>
											);
										})}
									</div>

									{/* Budget column spacer */}
									<div style={{ width: BUDGET_W, flexShrink: 0 }} />
								</div>

								{/* Expanded project sub-rows: active first, then inactive */}
								{isExp && (() => {
									const sortByName = (a, b) => (
										(projects.find(p => p.id === a)?.name ?? "").localeCompare(projects.find(p => p.id === b)?.name ?? "")
									);
									// Active projects before On Hold, alpha within each tier.
									const sortByStatusThenName = (a, b) => {
										const rankDelta =
											(PROJECT_STATUS_RANK[projects.find(p => p.id === a)?.status] ?? 9) -
											(PROJECT_STATUS_RANK[projects.find(p => p.id === b)?.status] ?? 9);
										return rankDelta !== 0 ? rankDelta : sortByName(a, b);
									};
									const isEmpOwner  = (pid) => {
										const p = projects.find(pr => pr.id === pid);
										if (!p) return false;
										if (p.manager_bqe_id && emp.bqeId) {
											return p.manager_bqe_id === emp.bqeId;
										}
										const managerName = normalizeName(p.manager);
										return !!managerName && [emp.name, emp.bqeName].some((name) => isPersonNameMatch(name, managerName));
									};
									const activePids  = emp.projects.filter(pid => projects.find(p => p.id === pid)?.status !== "other");
									const closedPids  = emp.projects.filter(pid => projects.find(p => p.id === pid)?.status === "other");
									const ownedPids   = activePids.filter(isEmpOwner);
									const contribPids = activePids.filter(pid => !isEmpOwner(pid));

									const renderSubRow = (pid, inactive) => {
										const proj = projects.find(p => p.id === pid);
										if (!proj) return null;
										const relationshipLabel = isEmpOwner(pid) ? "Owner" : "Contributor";
										const projLabel = [proj.name, proj.projectName, proj.phaseName].filter(Boolean).join(" · ");

										const empNorm = normalizeName(emp.name);
										const projBudgets = apiBudgets.filter(b => {
											if (b.project_id !== pid) return false;
											if (emp.bqeId && b.resource_id) return String(b.resource_id) === emp.bqeId;
											return normalizeName(b.employee) === empNorm;
										});
										const ridKey    = emp.bqeId ? `${emp.bqeId}|${pid}` : null;
										const spentH   = ridKey && spentByResourceId.has(ridKey)
											? spentByResourceId.get(ridKey)
											: spentByName.get(`${normalizeName(emp.name)}|${pid}`) || 0;
										const hasBudget = projBudgets.length > 0;
										const budgetH  = hasBudget ? projBudgets.reduce((s, b) => s + (b.budget_hours || 0), 0) : null;
										const remainH  = budgetH !== null ? budgetH - spentH : null;

										return (
											<div key={pid} style={{ display: "flex", alignItems: "stretch", height: SUB_ROW_H, background: inactive ? "#e2e5ef" : "#eaecf4", borderBottom: "1px solid #c8cdd8", borderLeft: "3px solid #b0b8d0", opacity: inactive ? 0.8 : 1 }}>
												<div style={{ width: LEFT_W - 3, flexShrink: 0, boxSizing: "border-box", display: "flex", alignItems: "stretch" }}>
													<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 8px 0 16px", minWidth: 0, gap: 2 }}>
														<Tooltip title={projLabel} placement="right" arrow enterDelay={400}>
															<a href={`/projects/viewer?recordId=${encodeURIComponent(proj.id)}&tab=schedule`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="wvProjectLink" style={{ fontSize: 12, fontWeight: 600 }}>
																{proj.name}
															</a>
														</Tooltip>
														{(proj.projectName || proj.phaseName) && (
															<div style={{ fontSize: 11, color: "#7a8190", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
																{[proj.projectName, proj.phaseName].filter(Boolean).join(" · ")}
															</div>
														)}
														<div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#586174", fontWeight: 600 }}>
															<span>{relationshipLabel}</span>
															{renderStatusChip(proj.status)}
														</div>

													</div>
												</div>
												{/* Dates: start / end / duration */}
												{(() => {
													const projBudget = projBudgets[0] ?? apiBudgets.find(b => b.project_id === pid);
													const durWks = projBudget?.duration_weeks;
													const durStr = durWks != null ? `${durWks} wk` : null;
													return (
														<div style={{ width: DATES_W, flexShrink: 0, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", fontSize: 11, lineHeight: 1.4, gap: 4, color: "#3a4051", borderRight: "1px solid #c8cdd8", padding: "0 8px" }}>
															<div>Start — {projBudget?.project_start ? <strong>{fmtShort(parseDate(projBudget.project_start))}</strong> : <span style={{ color: "#c62828" }}>missing</span>}</div>
															<div>End — {projBudget?.end_date ? <strong>{fmtShort(parseDate(projBudget.end_date))}</strong> : <span style={{ color: "#c62828" }}>missing</span>}</div>
															<div>Dur — {durStr ? <strong>{durStr}</strong> : <span style={{ color: "#c62828" }}>missing</span>}</div>
														</div>
													);
												})()
												}
												<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
											{/* Grid lines */}
											{buckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												if (lPx < 0 || lPx > trackW) return null;
												return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #e3e6eb", pointerEvents: "none" }} />;
											})}
											{/* Today line */}
											{todayPx >= 0 && todayPx <= trackW && (
												<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.4, pointerEvents: "none", zIndex: 3 }} />
											)}
											{/* Project start marker */}
											{proj.startDate && (() => {
												const px = dayToPx(daysBetween(fromDate, proj.startDate));
												if (px < -1 || px > trackW) return null;
												return (<React.Fragment>
													<div style={{ position: "absolute", left: px, top: 0, bottom: 0, borderLeft: "2px solid #4ea861", opacity: 0.55, pointerEvents: "none", zIndex: 2 }} />
													<div style={{ position: "absolute", left: px + 3, top: 3, padding: "1px 4px", background: "#dcfce7", border: "1px solid #4ea861", borderRadius: 3, fontSize: 10, fontWeight: 600, color: "#14532d", whiteSpace: "nowrap", zIndex: 4, pointerEvents: "none" }}>
														{fmtShort(proj.startDate)}
													</div>
												</React.Fragment>);
											})()}
											{/* Project end marker */}
											{proj.endDate && (() => {
												const px = dayToPx(daysBetween(fromDate, proj.endDate) + 1);
												if (px < 0 || px > trackW + 1) return null;
												return (<React.Fragment>
													<div style={{ position: "absolute", left: px, top: 0, bottom: 0, borderLeft: "2px solid #c62828", opacity: 0.55, pointerEvents: "none", zIndex: 2 }} />
													<div style={{ position: "absolute", left: px - 3, top: 3, transform: "translateX(-100%)", padding: "1px 4px", background: "#fee2e2", border: "1px solid #c62828", borderRadius: 3, fontSize: 10, fontWeight: 600, color: "#7f1d1d", whiteSpace: "nowrap", zIndex: 4, pointerEvents: "none" }}>
														{fmtShort(proj.endDate)}
													</div>
												</React.Fragment>);
											})()}
											{renderOnHoldBand(proj)}
											{/* Chips per bucket */}
											{buckets.map((b, bi) => {
												const lPx  = dayToPx(b.startDay);
												const wPx  = (b.endDay - b.startDay) * pxPerDay;
												if (lPx + wPx < 0 || lPx > trackW) return null;
												const agg   = aggregateRange(emp, b.startDate, b.endDate);
												const billH = Array.isArray(pid) ? pid.reduce((s, p) => s + (agg.projBill[p] || 0), 0) : (agg.projBill[pid] || 0);
												const nbH   = Array.isArray(pid) ? pid.reduce((s, p) => s + (agg.projNonbill[p] || 0), 0) : (agg.projNonbill[pid] || 0);
												const planH = zoom !== "day" ? (Array.isArray(pid) ? pid.reduce((s, p) => s + (agg.projPlan[p] || 0), 0) : (agg.projPlan[pid] || 0)) : 0;
												const showBillChip = showBillable && billH >= 0.05;
												const showNbChip   = showNonbill  && nbH  >= 0.05;
												const showPlanChip = showPlanned  && planH >= 0.05;
												if (!showBillChip && !showNbChip && !showPlanChip) return null;
												const cx = lPx + wPx / 2;
												return (
													<div key={bi} style={{ position: "absolute", left: cx, top: "50%", transform: "translate(-50%, -50%)", display: "flex", gap: 3, alignItems: "center", zIndex: 2 }}>
														{showBillChip && (
															<Tooltip arrow placement="top" title={`${projLabel}: ${fmtH(billH)}h billable`}>
																<div style={{ padding: "2px 7px", borderRadius: 6, background: "#b9f0c0", border: "1.5px solid #4ea861", color: "#1a3d23", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																{fmtH(billH)}
																</div>
															</Tooltip>
														)}
														{showNbChip && (
															<Tooltip arrow placement="top" title={`${projLabel}: ${fmtH(nbH)}h non-billable`}>
																<div style={{ padding: "2px 7px", borderRadius: 6, background: "#fecaca", border: "1.5px solid #ef5350", color: "#7f1d1d", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																{fmtH(nbH)}
																</div>
															</Tooltip>
														)}
														{showPlanChip && (
															<Tooltip arrow placement="top" title={`${projLabel}: ${fmtH(planH)}h planned`}>
																<div style={{ padding: "2px 7px", borderRadius: 6, background: "#bcd8ff", border: "1.5px solid #5b8dd6", color: "#19345e", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																{fmtH(planH)}
																</div>
															</Tooltip>
														)}
													</div>
												);
											})}
											{/* Plan week-bars in day zoom */}
											{showPlanned && zoom === "day" && planBuckets && planBuckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												const wPx = 5 * pxPerDay;
												if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
												const agg   = aggregateRange(emp, b.startDate, b.endDate);
												const planH = Array.isArray(pid) ? pid.reduce((s, p) => s + (agg.projPlan[p] || 0), 0) : (agg.projPlan[pid] || 0);
												if (planH < 0.05) return null;
												return (
													<Tooltip key={"pw" + bi} arrow placement="top" title={`${projLabel}: ${fmtH(planH)}h planned`}>
														<div style={{ position: "absolute", left: lPx + 4, width: wPx - 8, height: 20, top: "50%", transform: "translateY(-50%)", background: "#bcd8ff", backgroundImage: PLAN_HATCH, border: "1.5px solid #5b8dd6", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: "#19345e", overflow: "hidden", zIndex: 1, fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
															{fmtH(planH)}
														</div>
													</Tooltip>
												);
											})}
												</div>
											{/* Budget summary */}
											<div style={{ width: BUDGET_W, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", paddingRight: 10, fontSize: 11, lineHeight: 1.4, gap: 4, color: "#3a4051" }}>
												<div>
													Spent — <strong>{fmtH(spentH)}</strong>h
												</div>
												{budgetH !== null && (
													<>
														<div style={remainH < 0 ? { color: "#c62828" } : undefined}>
															Remaining — <strong>{fmtH(remainH)}</strong>h
														</div>
														<div>
															Budget — <strong>{fmtH(budgetH)}</strong>h
														</div>
													</>
												)}
											</div>
										</div>
									);
								};

									const aggLabel = `${closedPids.length} inactive project${closedPids.length !== 1 ? "s" : ""}`;

									return (
										<React.Fragment>
											{[...ownedPids].sort(sortByStatusThenName).map(pid => renderSubRow(pid, false))}
											{[...contribPids].sort(sortByStatusThenName).map(pid => renderSubRow(pid, false))}

											{/* Aggregated inactive row */}
											{closedPids.length > 0 && !showClosedProjects && (
												<div style={{ display: "flex", alignItems: "stretch", height: SUB_ROW_H, background: "#dde0ec", borderBottom: "1px solid #c8cdd8", borderLeft: "3px solid #b0b8d0" }}>
													<div style={{ width: LEFT_W - 3, flexShrink: 0, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 8px 0 16px" }}>
														<div style={{ fontSize: 11.5, color: "#7a8190", fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{aggLabel}</div>
													</div>
													<div style={{ width: DATES_W, flexShrink: 0, boxSizing: "border-box", borderRight: "1px solid #c8cdd8" }} />
													<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
											{/* Grid lines */}
											{buckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												if (lPx < 0 || lPx > trackW) return null;
												return <div key={bi} style={{ position: "absolute", left: lPx, top: 0, bottom: 0, borderLeft: "1px dashed #e3e6eb", pointerEvents: "none" }} />;
											})}
											{/* Today line */}
											{todayPx >= 0 && todayPx <= trackW && (
												<div style={{ position: "absolute", left: todayPx, top: 0, bottom: 0, borderLeft: "2px solid #184C93", opacity: 0.4, pointerEvents: "none", zIndex: 3 }} />
											)}
											{/* Chips per bucket */}
											{buckets.map((b, bi) => {
												const lPx  = dayToPx(b.startDay);
												const wPx  = (b.endDay - b.startDay) * pxPerDay;
												if (lPx + wPx < 0 || lPx > trackW) return null;
												const agg   = aggregateRange(emp, b.startDate, b.endDate);
												const billH = Array.isArray(closedPids) ? closedPids.reduce((s, p) => s + (agg.projBill[p] || 0), 0) : (agg.projBill[closedPids] || 0);
												const planH = zoom !== "day" ? (Array.isArray(closedPids) ? closedPids.reduce((s, p) => s + (agg.projPlan[p] || 0), 0) : (agg.projPlan[closedPids] || 0)) : 0;
												const showBillChip = showBillable && billH >= 0.05;
												const showPlanChip = showPlanned  && planH >= 0.05;
												if (!showBillChip && !showPlanChip) return null;
												const cx = lPx + wPx / 2;
												return (
													<div key={bi} style={{ position: "absolute", left: cx, top: "50%", transform: "translate(-50%, -50%)", display: "flex", gap: 3, alignItems: "center", zIndex: 2 }}>
														{showBillChip && (
															<Tooltip arrow placement="top" title={`${aggLabel}: ${fmtH(billH)}h billable`}>
																<div style={{ padding: "2px 7px", borderRadius: 6, background: "#b9f0c0", border: "1.5px solid #4ea861", color: "#1a3d23", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																{fmtH(billH)}
																</div>
															</Tooltip>
														)}
														{showPlanChip && (
															<Tooltip arrow placement="top" title={`${aggLabel}: ${fmtH(planH)}h planned`}>
																<div style={{ padding: "2px 7px", borderRadius: 6, background: "#bcd8ff", border: "1.5px solid #5b8dd6", color: "#19345e", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
																{fmtH(planH)}
																</div>
															</Tooltip>
														)}
													</div>
												);
											})}
											{/* Plan week-bars in day zoom */}
											{showPlanned && zoom === "day" && planBuckets && planBuckets.map((b, bi) => {
												const lPx = dayToPx(b.startDay);
												const wPx = 5 * pxPerDay;
												if (lPx + wPx < 0 || lPx > trackW || wPx <= 8) return null;
												const agg   = aggregateRange(emp, b.startDate, b.endDate);
												const planH = Array.isArray(closedPids) ? closedPids.reduce((s, p) => s + (agg.projPlan[p] || 0), 0) : (agg.projPlan[closedPids] || 0);
												if (planH < 0.05) return null;
												return (
													<Tooltip key={"pw" + bi} arrow placement="top" title={`${aggLabel}: ${fmtH(planH)}h planned`}>
														<div style={{ position: "absolute", left: lPx + 4, width: wPx - 8, height: 20, top: "50%", transform: "translateY(-50%)", background: "#bcd8ff", backgroundImage: PLAN_HATCH, border: "1.5px solid #5b8dd6", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: "#19345e", overflow: "hidden", zIndex: 1, fontVariantNumeric: "tabular-nums", userSelect: "none", cursor: "default" }}>
															{fmtH(planH)}
														</div>
													</Tooltip>
												);
											})}
													</div>
												</div>
											)}

											{/* Individual inactive rows with section header */}
											{closedPids.length > 0 && showClosedProjects && (
												<React.Fragment>
													<div style={{ display: "flex", alignItems: "center", height: 24, background: "#dde0ec", borderBottom: "1px solid #c8cdd8", borderLeft: "3px solid #b0b8d0", padding: "0 16px", fontSize: 10, fontWeight: 700, color: "#7a8190", textTransform: "uppercase", letterSpacing: ".05em" }}>
														Inactive projects
													</div>
													{[...closedPids].sort(sortByName).map(pid => renderSubRow(pid, true))}
												</React.Fragment>
											)}
										</React.Fragment>
									);
								})()}

								</React.Fragment>
							);
						})}
					</div>

					{/* ── Scrollbar ─────────────────────────────────────────────── */}
					<div style={{ borderTop: "1px solid #eef0f3", padding: "10px 16px", flexShrink: 0 }}>
						<TimelineScrollbar
							value={scrollDays}
							max={maxScroll}
							thumbFraction={Math.min(1, visibleDays / Math.max(1, totalDays))}
							onChange={setScrollDays}
							disabled={maxScroll <= 0}
						/>
					</div>

				</>)}
			</div>

			{/* ── Legend ───────────────────────────────────────────────────── */}
			<div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 10, alignItems: "center", flexShrink: 0 }}>
				<LegendSwatch color="#43a047" border="#2e7d32" label="Billable" />
				<LegendSwatch color="#ef5350" border="#c62828" label="Non-billable" />
				<LegendSwatch color="#9575cd" border="#6a3db5" label="Time off" />
				<LegendSwatch color="#f59e0b" border="#d97706" label="Company holiday" />
				<LegendSwatch color="#5b8dd6" border="#3a6ab5" backgroundImage={PLAN_HATCH} label="Planned" />
				<span style={{ width: 1, height: 14, background: "#d8dce3", margin: "0 2px" }} />
				<LegendSwatch color="#93c5fd" border="#2563eb" label="Under target (<65%)" />
				<LegendSwatch color="#86efac" border="#16a34a" label="On track (65–80%)" />
				<LegendSwatch color="#fdba74" border="#ea580c" label="Above target (80–90%)" />
				<LegendSwatch color="#fca5a5" border="#dc2626" label="Overloaded (>90%)" />
			</div>
		</div>
	);
};

export default WorkloadViewer;
